/**
 * Availability Component with Angular Calendar
 *
 * Zeigt die Availability für Dozenten an mit professioneller Kalenderintegration.
 * Ermöglicht es, Standorte per Drag-and-Drop zwischen zwei Listen zu verschieben.
 * Nur für Benutzer mit Dozenten-Rolle zugänglich.
 *
 * Features:
 * - Angular Calendar Integration
 * - Monatsansicht, Quartalsansicht, Semesteransicht
 * - Drag & Drop für Standorte und Modul-Varianten
 * - Verfügbarkeitsstatus: Normal, Verfügbar, Nicht verfügbar
 * - Feiertagsintegration
 */

import {
  Component,
  ChangeDetectionStrategy,
  inject,
  signal,
  OnInit,
  computed,
  LOCALE_ID,
} from '@angular/core';
import { CommonModule, registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import {
  DragDropModule,
  CdkDragDrop,
  moveItemInArray,
  transferArrayItem,
} from '@angular/cdk/drag-drop';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { forkJoin, switchMap } from 'rxjs';

// Angular Calendar imports
import { CalendarModule, DateAdapter, provideCalendar } from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';
import {
  CalendarEvent,
  CalendarEventAction,
  CalendarEventTimesChangedEvent,
  CalendarView,
} from 'angular-calendar';
import {
  startOfMonth,
  endOfMonth,
  startOfDay,
  endOfDay,
  addMonths,
  addDays,
  addWeeks,
  format,
  parseISO,
  isSameDay,
  isSameMonth,
  getWeek,
  differenceInMonths,
} from 'date-fns';
import { de } from 'date-fns/locale';

import { SchoolLocationService } from '../../core/services/school-location.service';
import { ModuleVariantService } from '../../core/services/module-variant.service';
import { SchoolService } from '../../core/services/school.service';
import { CalendarService } from '../../core/services/calendar.service';
import {
  LecturerAvailabilityService,
  LecturerAvailabilityFilters,
  ModuleVariantWithSlots,
  SchoolWithNestedDetails,
  LocationWithVariants,
  PlannedBlock,
  PlanningQuarter,
} from '../../core/services/lecturer-availability.service';
import { UserService } from '../../services/user.service';
import { ContactService } from '../../core/services/contact.service';
import { AuthService } from '../../services/auth.service';
import {
  SchoolLocationRead,
  ModuleVariantRead,
  SchoolRead,
  ModuleVariantSlot,
  Holiday,
} from '../../core/models';
import { DayCellDetailDialogComponent } from './day-cell-detail-dialog.component';

// Register German locale
registerLocaleData(localeDe);

// Availability status for calendar events
// Gray (normal) = No database entry
// Green (available) = Database entry with availability_date set
// Red (unavailable) = Database entry with availability_date NULL
export enum AvailabilityStatus {
  Normal = 'normal',
  Available = 'available',
  Unavailable = 'unavailable',
  Holiday = 'holiday',
  PublicHoliday = 'public-holiday',
  RegionalHoliday = 'regional-holiday',
  Draft = 'draft',
  Approved = 'approved',
}

// Calendar view types
export enum ViewMode {
  Month = 'month',
  Quarter = 'quarter',
  Semester = 'semester',
}

// Extended CalendarEvent with availability data
export interface AvailabilityEvent extends CalendarEvent {
  availabilityStatus: AvailabilityStatus;
  variantId: number;
  variantName: string;
  slotId: string;
  locationIds: number[];
  isHoliday?: boolean;
  holidayDescription?: string;
  holidayType?: 'public' | 'region'; // Type of holiday based on calendar_entry_type
  slots?: ModuleVariantSlot[]; // Multiple time slots for the same day
  isPlanned?: boolean;
  planningStatus?: string;
  isGuaranteed?: boolean;
  originalAvailabilityStatus?: AvailabilityStatus;
}

@Component({
  selector: 'app-availability',
  standalone: true,
  providers: [
    ...provideCalendar({
      provide: DateAdapter,
      useFactory: adapterFactory,
    }),
    {
      provide: LOCALE_ID,
      useValue: 'de',
    },
  ],
  imports: [
    CommonModule,
    DragDropModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatSelectModule,
    MatFormFieldModule,
    MatButtonToggleModule,
    MatButtonModule,
    MatTooltipModule,
    MatDialogModule,
    CalendarModule,
  ],
  template: `
    <div class="availability-container">
      @if (loading()) {
        <div class="loading">
          <mat-spinner diameter="40"></mat-spinner>
          <span>Lädt Verfügbarkeitsdaten...</span>
        </div>
      } @else if (error()) {
        <div class="error">
          <span class="material-symbols-outlined">error</span>
          <span>{{ error() }}</span>
        </div>
      } @else {
        <!-- Control Bar -->
        <div class="control-bar">
          <!-- School Selection -->
          @if (schools().length > 1) {
            <mat-form-field appearance="outline" class="school-dropdown">
              <mat-label>
                <span class="material-symbols-outlined text-sm align-middle mr-1">school</span>
                Schule auswählen
              </mat-label>
              <mat-select [(value)]="selectedSchoolId" (selectionChange)="onSchoolChange()">
                @for (school of schools(); track school.school_id) {
                  <mat-option [value]="school.school_id">
                    {{ school.school_shortname }} - {{ school.school_name }}
                  </mat-option>
                }
              </mat-select>
            </mat-form-field>
          } @else if (schools().length === 1) {
            <div class="school-display">
              <span class="material-symbols-outlined">school</span>
              <span class="school-name">
                {{ schools()[0].school_shortname }} - {{ schools()[0].school_name }}
              </span>
            </div>
          }

          <!-- View Mode Selector -->
          @if (!isListView()) {
            <div class="view-selector">
              <div class="inline-flex p-1 bg-gray-100 rounded-xl">
                <button (click)="onViewModeChange(ViewMode.Month)" 
                        [class.bg-white]="viewMode() === ViewMode.Month"
                        [class.shadow-sm]="viewMode() === ViewMode.Month"
                        class="flex items-center gap-2 px-4 py-1.5 rounded-lg transition-all text-sm font-medium hover:text-blue-600">
                  <span class="material-symbols-outlined text-lg">calendar_view_month</span>
                  <span>Monat</span>
                </button>
                <button (click)="onViewModeChange(ViewMode.Quarter)" 
                        [class.bg-white]="viewMode() === ViewMode.Quarter"
                        [class.shadow-sm]="viewMode() === ViewMode.Quarter"
                        class="flex items-center gap-2 px-4 py-1.5 rounded-lg transition-all text-sm font-medium hover:text-blue-600">
                  <span class="material-symbols-outlined text-lg">calendar_view_day</span>
                  <span>Quartal</span>
                </button>
                <button (click)="onViewModeChange(ViewMode.Semester)" 
                        [class.bg-white]="viewMode() === ViewMode.Semester"
                        [class.shadow-sm]="viewMode() === ViewMode.Semester"
                        class="flex items-center gap-2 px-4 py-1.5 rounded-lg transition-all text-sm font-medium hover:text-blue-600">
                  <span class="material-symbols-outlined text-lg">calendar_today</span>
                  <span>Semester</span>
                </button>
              </div>
            </div>
          }

          <!-- View Toggle Switcher (Calendar vs List) -->
          <div class="inline-flex p-1 bg-gray-100 rounded-xl ml-auto border border-slate-200 shadow-sm">
            <button (click)="isListView.set(false)" 
                    [class.bg-white]="!isListView()"
                    [class.shadow-sm]="!isListView()"
                    class="flex items-center gap-2 px-4 py-1.5 rounded-lg transition-all text-sm font-semibold hover:text-blue-600">
              <span class="material-symbols-outlined text-lg">calendar_month</span>
              <span>Kalenderansicht</span>
            </button>
            <button (click)="isListView.set(true)" 
                    [class.bg-white]="isListView()"
                    [class.shadow-sm]="isListView()"
                    class="flex items-center gap-2 px-4 py-1.5 rounded-lg transition-all text-sm font-semibold hover:text-blue-600">
              <span class="material-symbols-outlined text-lg">format_list_bulleted</span>
              <span>Listenansicht</span>
            </button>
          </div>

          <!-- Planungszeitraum Notice -->
          @if (!isListView()) {
            <div class="flex items-center gap-3 px-4 py-1.5 bg-blue-50/50 border border-blue-100 rounded-xl mx-auto shadow-sm">
              <span class="material-symbols-outlined text-blue-600 text-lg">info</span>
              <div class="flex flex-col">
                <span class="text-[10px] font-semibold text-blue-700 uppercase tracking-wider leading-tight">Planungszeitraum</span>
                <span class="text-sm font-bold text-blue-900 leading-tight">{{ planningPeriodRange() }}</span>
              </div>
            </div>

            <!-- Navigation -->
            <div class="calendar-navigation">
              <button class="p-2 rounded-xl hover:bg-gray-100 transition-colors disabled:opacity-30 flex items-center justify-center ring-1 ring-gray-200 shadow-sm bg-white" 
                      (click)="previousPeriod()" [disabled]="!canGoPrevious()">
                <span class="material-symbols-outlined">chevron_left</span>
              </button>
              <div class="period-label">
                <span class="period-title">{{ periodTitle() }}</span>
                <span class="period-range">{{ periodRange() }}</span>
              </div>
              <button class="p-2 rounded-xl hover:bg-gray-100 transition-colors disabled:opacity-30 flex items-center justify-center ring-1 ring-gray-200 shadow-sm bg-white" 
                      (click)="nextPeriod()" [disabled]="!canGoNext()">
                <span class="material-symbols-outlined">chevron_right</span>
              </button>
            </div>
          }
        </div>

        @if (isListView()) {
          <!-- Read-only List View (Keep only the planungszeitraum bar) -->
          <div class="list-view-container flex flex-col flex-1 min-h-0 overflow-hidden">
            <div class="list-view-period-bar flex items-center gap-3 px-4 py-2.5 bg-blue-50/50 border border-blue-100 rounded-xl max-w-xl mx-auto shadow-sm mb-6 mt-4">
              <span class="material-symbols-outlined text-blue-600">info</span>
              <div class="flex flex-col">
                <span class="text-[10px] font-bold text-blue-700 uppercase tracking-wider leading-tight">Planungszeitraum</span>
                <span class="text-sm font-extrabold text-blue-900 leading-tight">{{ planningPeriodRange() }}</span>
              </div>
            </div>

            <div class="list-table-card bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden flex-1 flex flex-col min-h-0">
              <div class="px-6 py-4 border-b border-slate-200 bg-slate-50/50 flex items-center justify-between">
                <h3 class="text-base font-bold text-slate-800 flex items-center gap-2">
                  <span class="material-symbols-outlined text-blue-600">list_alt</span>
                  Meine Verfügbarkeiten
                </h3>
                <span class="text-xs font-semibold text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full">
                  {{ sortedPlannedBlocks().length }} Einträge
                </span>
              </div>

              <div class="overflow-auto flex-1 p-6">
                @if (sortedPlannedBlocks().length === 0) {
                  <div class="flex flex-col items-center justify-center py-16 text-slate-400">
                    <span class="material-symbols-outlined text-5xl mb-3 text-slate-300">event_busy</span>
                    <h4 class="font-bold text-slate-600 mb-1">Keine Verfügbarkeiten eingetragen</h4>
                    <p class="text-sm">Für diesen Planungszeitraum wurden noch keine Verfügbarkeiten von Ihnen eingetragen.</p>
                  </div>
                } @else {
                  <div class="overflow-x-auto border border-slate-200 rounded-xl shadow-xs">
                    <table class="w-full text-left border-collapse">
                      <thead>
                        <tr class="bg-slate-50/75 border-b border-slate-200">
                          <th class="px-6 py-3.5 text-xs font-bold text-slate-500 uppercase tracking-wider">Datum</th>
                          <th class="px-6 py-3.5 text-xs font-bold text-slate-500 uppercase tracking-wider">Zeit</th>
                          <th class="px-6 py-3.5 text-xs font-bold text-slate-500 uppercase tracking-wider">Modulvariante</th>
                          <th class="px-6 py-3.5 text-xs font-bold text-slate-500 uppercase tracking-wider">Standort</th>
                          <th class="px-6 py-3.5 text-xs font-bold text-slate-500 uppercase tracking-wider">Status</th>
                        </tr>
                      </thead>
                      <tbody class="divide-y divide-slate-100">
                        @for (block of sortedPlannedBlocks(); track block.lecture_block_id) {
                          <tr class="hover:bg-slate-50/40 transition-colors">
                            <td class="px-6 py-4 text-sm font-semibold text-slate-700 whitespace-nowrap">
                              {{ block.date | date:'dd.MM.yyyy' }}
                            </td>
                            <td class="px-6 py-4 text-sm text-slate-600 whitespace-nowrap">
                              {{ block.begin_time.substring(0,5) }} - {{ block.end_time.substring(0,5) }} Uhr
                            </td>
                            <td class="px-6 py-4 text-sm text-slate-900 font-bold">
                              {{ block.module }}
                            </td>
                            <td class="px-6 py-4 text-sm text-slate-600 font-semibold whitespace-nowrap">
                              <span [class.bg-green-50]="block.location === 'ONLINE'"
                                    [class.text-green-700]="block.location === 'ONLINE'"
                                    [class.border-green-200]="block.location === 'ONLINE'"
                                    [class.border]="block.location === 'ONLINE'"
                                    [class.px-2.5]="block.location === 'ONLINE'"
                                    [class.py-0.5]="block.location === 'ONLINE'"
                                    [class.rounded-full]="block.location === 'ONLINE'"
                                    class="inline-flex items-center gap-1">
                                @if (block.location === 'ONLINE') {
                                  <span class="material-symbols-outlined text-[14px]">devices</span>
                                }
                                {{ block.location }}
                              </span>
                            </td>
                            <td class="px-6 py-4 text-sm whitespace-nowrap">
                              <div class="flex items-center gap-2">
                                @if (block.status === 'available') {
                                  <span class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide border border-green-200 bg-green-50 text-green-700 shadow-sm">
                                    <span class="material-symbols-outlined text-[12px] font-bold">check_circle</span>
                                    Verfügbar
                                  </span>
                                } @else {
                                  <span class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide border border-red-200 bg-red-50 text-red-700 shadow-sm">
                                    <span class="material-symbols-outlined text-[12px] font-bold">cancel</span>
                                    Nicht verfügbar
                                  </span>
                                }
                                
                                @if (block.isPlanned) {
                                  <span class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide border border-blue-200 bg-blue-50 text-blue-800 shadow-sm"
                                        [title]="'Planung: ' + block.plannedModule">
                                    <span class="material-symbols-outlined text-[12px] font-bold">event</span>
                                    Geplant
                                  </span>
                                }
                              </div>
                            </td>
                          </tr>
                        }
                      </tbody>
                    </table>
                  </div>
                }
              </div>
            </div>
          </div>
        } @else {
        <!-- Drag & Drop Sections -->
        <div class="sections-grid">
          <!-- Standorte Section -->
          <div class="section">
            <h2 class="section-title">
              <span class="material-symbols-outlined">location_on</span>
              Standorte
            </h2>
            <div class="lists">
              <div class="list-section">
                <div class="list-header">
                  <span class="title">Verfügbar</span>
                  <span class="count">{{ availableLocations().length }}</span>
                </div>
                <div
                  cdkDropList
                  #availableList="cdkDropList"
                  [cdkDropListData]="availableLocations()"
                  [cdkDropListConnectedTo]="[selectedList]"
                  cdkDropListSortingDisabled
                  class="drop-zone"
                  (cdkDropListDropped)="dropLocation($event)"
                >
                  @for (location of availableLocations(); track location.location_id) {
                    <div class="item" cdkDrag>
                      <div class="placeholder" *cdkDragPlaceholder></div>
                      <span class="material-symbols-outlined handle">drag_indicator</span>
                      <span class="shortname">
                        {{ location.location_shortname }}
                        @if (location.town) {
                          - {{ location.town }}
                        }
                      </span>
                    </div>
                  } @empty {
                    <div class="empty">
                      <span class="material-symbols-outlined">inbox</span>
                      <span>Alle ausgewählt</span>
                    </div>
                  }
                </div>
              </div>

              <div class="list-section selected">
                <div class="list-header">
                  <span class="title">Meine Standorte</span>
                  <span class="count">{{ selectedLocations().length }}</span>
                </div>
                <div
                  cdkDropList
                  #selectedList="cdkDropList"
                  [cdkDropListData]="selectedLocations()"
                  [cdkDropListConnectedTo]="[availableList]"
                  class="drop-zone"
                  (cdkDropListDropped)="dropLocation($event)"
                >
                  @for (
                    location of selectedLocations();
                    track location.location_id;
                    let idx = $index
                  ) {
                    <div class="item active" cdkDrag>
                      <div class="placeholder" *cdkDragPlaceholder></div>
                      <span class="number">{{ idx + 1 }}</span>
                      <span class="shortname">
                        {{ location.location_shortname }}
                        @if (location.town) {
                          - {{ location.town }}
                        }
                      </span>
                    </div>
                  } @empty {
                    <div class="empty">
                      <span class="material-symbols-outlined">add_circle</span>
                      <span>Hierher ziehen</span>
                    </div>
                  }
                </div>
              </div>
            </div>
          </div>

          <!-- Module Varianten Section -->
          <div class="section">
            <h2 class="section-title">
              <span class="material-symbols-outlined">schedule</span>
              Modul-Varianten
            </h2>
            <div class="lists">
              <div class="list-section">
                <div class="list-header">
                  <span class="title">Verfügbar</span>
                  <span class="count">{{ availableVariants().length }}</span>
                </div>
                <div
                  cdkDropList
                  #availableVariantsList="cdkDropList"
                  [cdkDropListData]="availableVariants()"
                  [cdkDropListConnectedTo]="[selectedVariantsList]"
                  cdkDropListSortingDisabled
                  class="drop-zone"
                  (cdkDropListDropped)="dropVariant($event)"
                >
                  @for (variant of availableVariants(); track variant.module_variant_id) {
                    <div class="item" cdkDrag>
                      <div class="placeholder" *cdkDragPlaceholder></div>
                      <span class="material-symbols-outlined handle">drag_indicator</span>
                      <div class="variant-info">
                        <span class="variant-name">
                          @if (variant.degree_codes) {
                            <span class="degree-badge">{{ variant.degree_codes }}</span>
                          }
                          {{ variant.variant_name }}
                        </span>
                        @if (variant.slots && variant.slots.length > 0) {
                          <span class="variant-times">{{ formatVariantTimes(variant) }}</span>
                        }
                      </div>
                    </div>
                  } @empty {
                    <div class="empty">
                      <span class="material-symbols-outlined">inbox</span>
                      <span>Alle ausgewählt</span>
                    </div>
                  }
                </div>
              </div>

              <div class="list-section selected">
                <div class="list-header">
                  <span class="title">Meine Varianten</span>
                  <span class="count">{{ selectedVariants().length }}</span>
                </div>
                <div
                  cdkDropList
                  #selectedVariantsList="cdkDropList"
                  [cdkDropListData]="selectedVariants()"
                  [cdkDropListConnectedTo]="[availableVariantsList]"
                  class="drop-zone"
                  (cdkDropListDropped)="dropVariant($event)"
                >
                  @for (
                    variant of selectedVariants();
                    track variant.module_variant_id;
                    let idx = $index
                  ) {
                    <div class="item active" cdkDrag>
                      <div class="placeholder" *cdkDragPlaceholder></div>
                      <span class="number">{{ idx + 1 }}</span>
                      <div class="variant-info">
                        <span class="variant-name">
                          @if (variant.degree_codes) {
                            <span class="degree-badge">{{ variant.degree_codes }}</span>
                          }
                          {{ variant.variant_name }}
                        </span>
                        @if (variant.slots && variant.slots.length > 0) {
                          <span class="variant-times">{{ formatVariantTimes(variant) }}</span>
                        }
                      </div>
                    </div>
                  } @empty {
                    <div class="empty">
                      <span class="material-symbols-outlined">add_circle</span>
                      <span>Hierher ziehen</span>
                    </div>
                  }
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Calendar Section -->
        @if (hasValidSelection()) {
          <!-- Cell Template (Used by all views for consistency) -->
          <ng-template
            #monthCellTemplate
            let-day="day"
            let-locale="locale"
            let-tooltipPlacement="tooltipPlacement"
            let-highlightDay="highlightDay"
            let-unhighlightDay="unhighlightDay"
          >
            <div
              class="cal-cell-top"
              [class.cal-has-events]="day.events.length > 0"
              [class.cal-weekend]="day.isWeekend"
              [class.cal-today]="day.isToday"
              [class.cal-out-month]="!day.inMonth"
              [class.cal-clickable]="day.events.length > 0"
              (mouseenter)="highlightDay.emit({ event: $event })"
              (mouseleave)="unhighlightDay.emit({ event: $event })"
            >
              <span class="cal-day-number">{{ day.date | date: 'd' }}</span>
            </div>
            <div class="cal-events">
              @if (day.events.length > 0) {
                <div class="events-summary">
                  @for (event of day.events; track event.id) {
                    <div
                      class="cal-event-badge"
                      [class.normal]="
                        getAvailabilityEvent(event).availabilityStatus === 'normal'
                      "
                      [class.available]="
                        getAvailabilityEvent(event).availabilityStatus === 'available'
                      "
                      [class.unavailable]="
                        getAvailabilityEvent(event).availabilityStatus === 'unavailable'
                      "
                      [class.holiday]="getAvailabilityEvent(event).isHoliday"
                      [class.draft]="
                        getAvailabilityEvent(event).isPlanned &&
                        getAvailabilityEvent(event).planningStatus === 'DRAFT'
                      "
                      [class.planning-approved]="
                        getAvailabilityEvent(event).isPlanned &&
                        getAvailabilityEvent(event).planningStatus === 'APPROVED'
                      "
                      [class.planning-provisional]="
                        getAvailabilityEvent(event).isPlanned &&
                        getAvailabilityEvent(event).planningStatus === 'CONFIRMED' &&
                        !getAvailabilityEvent(event).isGuaranteed
                      "
                      [class.planning-guaranteed]="
                        getAvailabilityEvent(event).isPlanned &&
                        getAvailabilityEvent(event).planningStatus === 'CONFIRMED' &&
                        getAvailabilityEvent(event).isGuaranteed
                      "
                      [matTooltip]="
                        getAvailabilityEvent(event).isHoliday ? getEventTooltip(event) : ''
                      "
                      (click)="handleEventClick({ event: event }); $event.stopPropagation()"
                    >
                      <div class="event-date">{{ formatEventDate(event) }}</div>
                      <div class="event-time">{{ formatEventTimeRange(event) }}</div>
                      @if (getAvailabilityEvent(event).availabilityStatus === 'unavailable') {
                        <div class="event-status">
                          <span class="material-symbols-outlined leading-none">close</span>
                        </div>
                      }
                      @if (getAvailabilityEvent(event).availabilityStatus === 'available' || getAvailabilityEvent(event).availabilityStatus === 'normal') {
                        <div class="event-status">
                          <span class="material-symbols-outlined leading-none">check</span>
                        </div>
                      }
                      @if (getAvailabilityEvent(event).isHoliday) {
                        <div class="event-label">
                          {{ getAvailabilityEvent(event).holidayDescription }}
                        </div>
                      }
                    </div>
                  }
                </div>
              }
            </div>
          </ng-template>

          <div class="calendar-section">
            @if (viewMode() === ViewMode.Month) {
              <!-- Month View -->
              <div class="calendar-wrapper month-view">
                <div class="month-header">
                  {{ getMonthName(viewDate()) }}
                </div>
                <mwl-calendar-month-view
                  [viewDate]="viewDate()"
                  [events]="calendarEvents()"
                  [locale]="locale"
                  [weekStartsOn]="weekStartsOn"
                  [activeDayIsOpen]="false"
                  (dayClicked)="handleDayClick($event)"
                  (eventClicked)="handleEventClick($event)"
                  (eventTimesChanged)="handleEventTimesChanged($event)"
                  [cellTemplate]="monthCellTemplate"
                >
                </mwl-calendar-month-view>
              </div>
            } @else if (viewMode() === ViewMode.Quarter) {
              <!-- Quarter View: Stacked for clarity -->
              <div class="quarter-view">
                @for (monthOffset of visibleMonthOffsets(); track monthOffset) {
                  <div class="month-in-quarter">
                    <div class="mini-month-header">
                      {{ getMonthName(addMonths(viewDate(), monthOffset)) }}
                    </div>
                    <mwl-calendar-month-view
                      [viewDate]="addMonths(viewDate(), monthOffset)"
                      [events]="getEventsForMonth(monthOffset)"
                      [locale]="locale"
                      [weekStartsOn]="weekStartsOn"
                      [activeDayIsOpen]="false"
                      (dayClicked)="handleDayClick($event)"
                      (eventClicked)="handleEventClick($event)"
                      [cellTemplate]="monthCellTemplate"
                    >
                    </mwl-calendar-month-view>
                  </div>
                }
              </div>
            } @else if (viewMode() === ViewMode.Semester) {
              <!-- Semester View: Stacked for clarity -->
              <div class="semester-view">
                @for (monthOffset of visibleMonthOffsets(); track monthOffset) {
                  <div class="month-in-semester">
                    <div class="mini-month-header">
                      {{ getMonthName(addMonths(viewDate(), monthOffset)) }}
                    </div>
                    <mwl-calendar-month-view
                      [viewDate]="addMonths(viewDate(), monthOffset)"
                      [events]="getEventsForMonth(monthOffset)"
                      [locale]="locale"
                      [weekStartsOn]="weekStartsOn"
                      [activeDayIsOpen]="false"
                      (dayClicked)="handleDayClick($event)"
                      (eventClicked)="handleEventClick($event)"
                      [cellTemplate]="monthCellTemplate"
                    >
                    </mwl-calendar-month-view>
                  </div>
                }
              </div>
            }
          </div>

          <!-- Legend (Stay at bottom, outside scrollable area) -->
          <div class="calendar-legend">
            <div class="legend-item">
              <div class="legend-box available"></div>
              <span>Verfügbar</span>
            </div>
            <div class="legend-item">
              <div class="legend-box unavailable"></div>
              <span>Nicht verfügbar</span>
            </div>
            <div class="legend-item">
              <div class="legend-box public-holiday"></div>
              <span>Globaler Feiertag</span>
            </div>
            <div class="legend-item">
              <div class="legend-box regional-holiday"></div>
              <span>Regionaler Feiertag</span>
            </div>
            <div class="legend-item">
              <div class="legend-box draft"></div>
              <span>Entwurf (DRAFT)</span>
            </div>
            <div class="legend-item">
              <div class="legend-box planning-approved"></div>
              <span>Freigegeben (APPROVED)</span>
            </div>
            <div class="legend-item">
              <div class="legend-box planning-provisional"></div>
              <span>Bestätigt (Provisorisch)</span>
            </div>
            <div class="legend-item">
              <div class="legend-box planning-guaranteed"></div>
              <span>Bestätigt (Garantiert)</span>
            </div>
          </div>
        } @else {
          <div class="empty-state">
            <span class="material-symbols-outlined">event_busy</span>
            <h3>Keine Auswahl</h3>
            <p>Bitte wählen Sie mindestens einen Standort und eine Modul-Variante aus.</p>
          </div>
        }
      }
      }
    </div>
  `,
  styles: `
    .availability-container {
      padding: 1rem;
      max-width: 100%;
      width: 100%;
      height: 100%;
      flex: 1;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      min-height: 0;
      position: relative;
    }

    .header {
      margin-bottom: 0.75rem;

      h1 {
        margin: 0 0 0.25rem 0;
        font-size: 1.5rem;
        color: #1976d2;
      }

      p {
        margin: 0;
        font-size: 0.8rem;
        color: #666;
      }
    }

    .loading,
    .error {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 1rem;
      padding: 2rem;
      border-radius: 8px;
      background: #f5f5f5;
    }
    
    .error {
      background: #ffebee;
      color: #c62828;
      
      .material-symbols-outlined {
        color: #c62828;
      }
    }

    .control-bar {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      margin-bottom: 0.75rem;
      padding: 0.75rem;
      background: white;
      border-radius: 6px;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      flex-wrap: wrap;
    }

    .school-dropdown {
      min-width: 250px;

      mat-label {
        display: flex;
        align-items: center;
        gap: 0.5rem;
      }
    }

    .school-display {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.5rem 1rem;
      background: #e3f2fd;
      border-radius: 12px;
      color: #1976d2;
      border: 1px solid #bbdefb;
      transition: all 0.2s ease;

      .material-symbols-outlined {
        font-size: 1.25rem;
      }

      .school-name {
        font-size: 0.9rem;
      }
    }

    .view-selector {
      mat-button-toggle {
        display: flex;
        align-items: center;
        gap: 0.5rem;
      }
    }

    .calendar-navigation {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-left: auto;
    }

    .period-label {
      display: flex;
      flex-direction: column;
      align-items: center;
      min-width: 200px;
      padding: 0 1rem;
    }

    .period-title {
      font-size: 1.25rem;
      font-weight: 700;
      color: #1976d2;
    }

    .period-range {
      font-size: 0.85rem;
      color: #666;
    }

    .sections-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 1.5rem;
      margin-bottom: 1.5rem;
    }

    @media (max-width: 1200px) {
      .sections-grid {
        grid-template-columns: 1fr;
      }
    }

    .section-title {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin: 0 0 1rem 0;
      font-size: 1rem;
      color: #333;

      .material-symbols-outlined {
        color: #1976d2;
        font-size: 1.25rem;
      }
    }

    .lists {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 1rem;
    }

    .list-section {
      border: 2px solid #e0e0e0;
      border-radius: 8px;
      background: white;
      overflow: hidden;

      &.selected {
        border-color: #4caf50;

        .list-header {
          background: #e8f5e9;
        }
      }
    }

    .list-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0.75rem 1rem;
      background: #f5f5f5;
      border-bottom: 2px solid #e0e0e0;

      .title {
        font-size: 0.875rem;
      }

      .count {
        background: #1976d2;
        color: white;
        padding: 0.25rem 0.5rem;
        border-radius: 12px;
        font-size: 0.75rem;
      }
    }

    .selected .list-header .count {
      background: #4caf50;
    }

    .drop-zone {
      min-height: 180px;
      max-height: 180px;
      overflow-y: auto;
      padding: 0.5rem;

      &.cdk-drop-list-dragging {
        background: #f0f7ff;
      }
    }

    .item {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.5rem;
      margin-bottom: 0.5rem;
      background: white;
      border: 2px solid #e0e0e0;
      border-radius: 6px;
      cursor: move;
      transition: all 0.2s ease;

      &:hover {
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        transform: translateX(4px);
      }

      &.active {
        background: #e8f5e9;
        border-color: #4caf50;
      }

      &.cdk-drag-preview {
        box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
        opacity: 0.95;
      }
    }

    .placeholder {
      background: #e3f2fd;
      border: 2px dashed #1976d2;
      min-height: 45px;
      border-radius: 6px;
    }

    .handle {
      color: #999;
      cursor: grab;
      font-size: 1.25rem;

      &:active {
        cursor: grabbing;
      }
    }

    .number {
      display: flex;
      align-items: center;
      justify-content: center;
      min-width: 24px;
      height: 24px;
      background: #4caf50;
      color: white;
      border-radius: 50%;
      font-size: 0.75rem;
    }

    .variant-info {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      flex: 1;
    }

    .variant-name {
      font-size: 0.875rem;

      .degree-badge {
        display: inline-block;
        background: #e3f2fd;
        color: #1565c0;
        border: 1px solid #90caf9;
        border-radius: 3px;
        padding: 0.05rem 0.3rem;
        font-size: 0.7rem;
        font-weight: 700;
        margin-right: 0.35rem;
        letter-spacing: 0.02em;
        vertical-align: middle;
      }
    }

    .variant-times {
      font-size: 0.75rem;
      color: #666;
    }

    .shortname {
      font-size: 0.875rem;
      flex: 1;
    }

    .empty {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 2rem 1rem;
      color: #999;
      text-align: center;
      gap: 0.5rem;

      .material-symbols-outlined {
        font-size: 48px;
        width: 48px;
        height: 48px;
        opacity: 0.3;
      }
    }

    .empty-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 4rem 2rem;
      text-align: center;
      color: #999;

      .material-symbols-outlined {
        font-size: 72px;
        width: 72px;
        height: 72px;
        margin-bottom: 1rem;
        opacity: 0.3;
      }

      h3 {
        margin: 0 0 0.5rem 0;
        font-size: 1.5rem;
        color: #666;
      }

      p {
        margin: 0;
        font-size: 1rem;
      }
    }

    .calendar-section {
      background: white;
      border-radius: 6px;
      padding: 0.75rem;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      overflow-y: auto;
      scroll-snap-type: y mandatory;
      display: flex;
      flex-direction: column;
      flex: 1;
      min-height: 0;
    }

    .calendar-wrapper {
      margin: 0 auto 0.75rem auto;
      width: 100%;
      max-width: 1200px;
      display: flex;
      flex-direction: column;
      min-height: 0;
    }

    // Angular Calendar Custom Styles
    ::ng-deep {
      .cal-month-view {
        background-color: white;
        flex: 1;
        display: flex;
        flex-direction: column;
        min-height: 0;

        .cal-days {
          flex: 1;
          display: flex;
          flex-direction: column;
          min-height: 0;
          border: none;
        }

        .cal-cell-row {
          flex: 1;
          display: flex;
          min-height: 0;
          border-bottom: 1px solid #e0e0e0;

          &:last-child {
            border-bottom: none;
          }
        }
        .cal-header {
          background: #1976d2 !important;
          color: white !important;

          .cal-cell {
            padding: 0.3rem;
            font-size: 1rem;
            background: #1976d2 !important;
            color: white !important;
            cursor: default !important;

            &:hover,
            &:focus,
            &:active {
              background: #1976d2 !important;
              color: white !important;
            }
          }

          .cal-cell-row {
            background: #1976d2 !important;
          }
        }

        .cal-cell {
          min-height: 0;
          flex: 1;
          border: 1px solid #e0e0e0;
          padding: 0;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        .cal-day-cell {
          padding: 0;

          &.cal-today {
            background-color: #e3f2fd !important;

            .cal-day-number {
              background: #1976d2;
              color: white;
              border-radius: 50%;
              padding: 0.1rem 0.3rem;
              font-size: 1rem;
              font-weight: 700;
            }
          }

          &.cal-out-month {
            opacity: 0.3;
          }

          &.cal-weekend {
            background-color: #fafafa;
          }
        }
      }
    }

    .cal-events {
      display: flex;
      flex-direction: column;
      gap: 2px;
      padding: 0 0.15rem 0.2rem 0.15rem;
      align-items: flex-start;
      max-height: calc(100% - 1.75rem);
      overflow-y: auto !important;
      overflow-x: hidden;

      /* Standard scrollbar for consistent look */
      &::-webkit-scrollbar {
        width: 4px;
      }
      &::-webkit-scrollbar-thumb {
        background: #e0e0e0;
        border-radius: 2px;
      }

      &.mini {
        flex-direction: row;
        flex-wrap: wrap;
        justify-content: center;
        align-items: center;
        gap: 1px;
        padding: 0.1rem;
        overflow-y: auto;
      }
    }

    .events-summary {
      display: flex;
      flex-direction: column;
      gap: 2px;
      width: 100%;
    }

    .events-list-mini {
      display: flex;
      flex-direction: column;
      gap: 0;
      width: 100%;
    }

    // Minimize day number area
    ::ng-deep .cal-cell-top {
      min-height: 1.25rem !important;
      max-height: 1.25rem !important;
      height: 1.25rem !important;
      padding: 0 !important;
      margin: 0 !important;
      display: flex;
      align-items: flex-start;
      justify-content: flex-start;

      &.cal-clickable {
        cursor: pointer;
        transition: background-color 0.2s ease;

        &:hover {
          background-color: rgba(25, 118, 210, 0.08);
        }
      }

      .cal-day-number {
        font-size: 1rem;
        font-weight: 700;
        line-height: 1;
        padding: 0.3rem 0.5rem;
        margin: 0;
      }
    }

    .cal-event-badge {
      padding: 0.2rem 0.25rem;
      border-radius: 3px;
      font-size: 0.8rem;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.15s ease;
      border: 1px solid;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
      display: flex;
      flex-direction: column;
      gap: 0.05rem;
      position: relative;
      line-height: 1.1;

      &.mini {
        padding: 0.2rem 0.25rem;
        border-width: 1px;
        font-size: 0.8rem;
        gap: 0;

        .event-date {
          font-size: 0.75rem;
        }

        .event-time {
          font-size: 0.7rem;
        }

        .event-status {
          font-size: 0.75rem;
          top: 0.05rem;
          right: 0.05rem;
        }
      }

      &.tiny {
        padding: 0.2rem 0.25rem;
        border-width: 1px;
        font-size: 0.8rem;
        gap: 0;

        .event-time {
          font-size: 0.7rem;
        }

        .event-status {
          font-size: 0.75rem;
          top: 0.05rem;
          right: 0.05rem;
        }
      }

      &.normal {
        background: #f5f5f5;
        border-color: #bdbdbd;
        color: #424242;
      }

      &.available {
        background: #c8e6c9;
        border-color: #4caf50;
        color: #1b5e20;
      }

      &.unavailable {
        background: #ffcdd2;
        border-color: #f44336;
        color: #b71c1c;
      }

      &.holiday {
        background: #fff9c4;
        border-color: #f57f17;
        color: #f57f17;
        cursor: not-allowed;
      }

      &.public-holiday {
        background: #fff9c4;
        border-color: #f57f17;
        color: #f57f17;
        cursor: not-allowed;
      }

      &.regional-holiday {
        background: #ffe0b2;
        border-color: #f57c00;
        color: #f57c00;
        cursor: not-allowed;
      }

      &.draft {
        background: #ffedd5;
        border-color: #f97316;
        color: #c2410c;
        cursor: not-allowed;
      }

      &.planning-approved {
        background: #f3e8ff;
        border-color: #a855f7;
        color: #7e22ce;
        cursor: not-allowed;
      }

      &.planning-provisional {
        background: #e0f2fe;
        border-color: #0284c7;
        color: #0369a1;
        cursor: not-allowed;
      }

      &.planning-guaranteed {
        background: #e0e7ff;
        border-color: #4f46e5;
        color: #3730a3;
        cursor: not-allowed;
      }

      &:hover:not(.holiday):not(.public-holiday):not(.regional-holiday):not(.draft):not(.planning-approved):not(.planning-provisional):not(.planning-guaranteed) {
        transform: translateY(-1px);
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
      }

      .event-date {
        font-size: 0.75rem;
        line-height: 1.1;
      }

      .event-time {
        font-size: 0.7rem;
        line-height: 1.1;
      }

      .event-times {
        display: flex;
        flex-direction: column;
        gap: 0.05rem;
      }

      .time-slot {
        font-weight: 500;
        font-size: 0.7rem;
        line-height: 1.1;
      }

      &.multi-slot {
        padding: 0.25rem 0.3rem;
      }

      .event-status {
        position: absolute;
        top: 0.1rem;
        right: 0.1rem;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 1.25rem;
        height: 1.25rem;
        font-weight: 900;
        z-index: 2;

        span {
          font-size: 1.1rem;
          line-height: 1;
        }
      }

      &.available .event-status { 
        color: #1b5e20 !important; 
      }
      &.unavailable .event-status { 
        color: #b71c1c !important; 
      }

      &.mini, &.tiny {
        .event-status {
          top: 0;
          right: 0;
          width: 0.8rem;
          height: 0.8rem;
          
          span {
            font-size: 0.75rem;
          }
        }
      }

      &.tiny .event-status span {
        font-size: 0.6rem;
      }
    }

    .more-badge {
      padding: 0.2rem 0.4rem;
      border-radius: 3px;
      font-size: 0.65rem;
      background: #e0e0e0;
      color: #424242;
      text-align: center;
      cursor: pointer;
      transition: all 0.2s ease;

      &.mini {
        padding: 0.15rem 0.25rem;
        font-size: 0.55rem;
      }

      &.tiny {
        padding: 0.1rem 0.2rem;
        font-size: 0.5rem;
      }

      &:hover {
        background: #bdbdbd;
        transform: translateY(-1px);
      }
    }

    .events-count-badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 2.15rem;
      height: 2.15rem;
      padding: 0.25rem;
      border-radius: 6px;
      font-size: 1rem;
      font-weight: 700;
      cursor: pointer;
      transition: all 0.2s ease;
      border: 2px solid;
      background: #e3f2fd;
      border-color: #1976d2;
      color: #0d47a1;

      &.small {
        min-width: 1.7rem;
        height: 1.7rem;
        font-size: 0.8rem;
        border-width: 1.5px;
      }

      &.has-available {
        background: #c8e6c9;
        border-color: #4caf50;
        color: #1b5e20;
      }

      &.has-unavailable {
        background: #ffcdd2;
        border-color: #f44336;
        color: #b71c1c;
      }

      &.has-holiday {
        background: #fff9c4;
        border-color: #f57f17;
        color: #f57f17;
      }

      &.has-public-holiday {
        background: #fff9c4;
        border-color: #f57f17;
        color: #f57f17;
      }

      &.has-regional-holiday {
        background: #ffe0b2;
        border-color: #f57c00;
        color: #f57c00;
      }

      &:hover {
        transform: scale(1.15);
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
      }
    }

    .quarter-view,
    .semester-view {
      display: grid;
      gap: 0.75rem;
      margin-bottom: 0.75rem;
    }

    .quarter-view {
      grid-template-columns: 1fr;
    }

    .semester-view {
      grid-template-columns: 1fr;
    }

    .month-in-quarter,
    .month-in-semester {
      border: 1px solid #e0e0e0;
      border-radius: 8px;
      padding: 1.5rem;
      background: white;
      scroll-snap-align: start;
      scroll-margin-top: 0.75rem;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
      margin-bottom: 2rem;
    }

    .mini-month-header {
      font-size: 1.5rem;
      font-weight: 700;
      color: #1976d2;
      text-align: center;
      margin-bottom: 0.75rem;
      padding-bottom: 0;
      border-bottom: none;
    }


    .calendar-section {
      flex: 1;
      overflow-y: auto !important;
      padding: 1rem;
      min-height: 0;
      display: flex;
      flex-direction: column;
      gap: 1.5rem;
      padding-bottom: 2.5rem;
      scroll-snap-type: y proximity;
    }

    .month-header {
      font-size: 1.5rem;
      font-weight: 700;
      color: #1976d2;
      text-align: center;
      margin-bottom: 0.75rem;
      padding-bottom: 0;
      border-bottom: none;
    }

    ::ng-deep {
      .cal-month-view .cal-cell {
        min-height: 3.5rem;
        max-height: 105px;
        padding: 0;
        overflow: hidden;
        transition: height 0.2s ease;

        .cal-events {
          max-height: calc(100% - 1.75rem);
          overflow-y: auto !important;

          /* Standard scrollbar for consistent look */
          &::-webkit-scrollbar {
            width: 4px;
          }
          &::-webkit-scrollbar-thumb {
            background: #e0e0e0;
            border-radius: 2px;
          }
        }
      }

      .cal-day-number {
        font-size: 1rem;
        font-weight: 700;
        color: #333;
      }
    }

    /* Responsive adjustments removed - now always 1 column for clarity */

    @media (max-width: 1024px) {
      .quarter-view,
      .semester-view {
        grid-template-columns: 1fr;
      }
    }

    .calendar-legend {
      display: flex;
      gap: 2rem;
      padding: 1rem;
      background: white;
      border-top: 1px solid #e0e0e0;
      justify-content: center;
      flex-wrap: wrap;
      position: sticky;
      bottom: 0;
      z-index: 10;
      width: 100%;
    }

    .legend-item {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 0.875rem;
    }

    .legend-box {
      width: 24px;
      height: 24px;
      border-radius: 4px;
      border: 2px solid;

      &.normal {
        background: #f5f5f5;
        border-color: #bdbdbd;
      }

      &.available {
        background: #c8e6c9;
        border-color: #4caf50;
      }

      &.unavailable {
        background: #ffcdd2;
        border-color: #f44336;
      }

      &.holiday {
        background: #fff9c4;
        border-color: #f57f17;
      }

      &.public-holiday {
        background: #fff9c4;
        border-color: #f57f17;
      }

      &.regional-holiday {
        background: #ffe0b2;
        border-color: #f57c00;
      }

      &.draft {
        background: #ffedd5;
        border-color: #f97316;
      }

      &.planning-approved {
        background: #f3e8ff;
        border-color: #a855f7;
      }

      &.planning-provisional {
        background: #e0f2fe;
        border-color: #0284c7;
      }

      &.planning-guaranteed {
        background: #e0e7ff;
        border-color: #4f46e5;
      }
    }

    // Enable better zoom support for calendar cells
    .calendar-section {
      flex: 1;
      overflow-y: auto;
      padding: 1rem;
      min-height: 0;
      display: flex;
      flex-direction: column;
      gap: 1rem;

      // Ensure content doesn't get cut off by legend
      padding-bottom: 2rem;
    }

    .cal-month-view {
      zoom: 1;
      transform-origin: top left;
    }

    // Make cells and badges scale properly on browser zoom
    @media (min-resolution: 120dpi) {
      .cal-event-badge {
        font-size: max(0.65rem, 10px);
      }

      .cal-day-number {
        font-size: max(0.7rem, 11px);
      }
    }

    .list-view-container {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-height: 0;
      width: 100%;
      height: 100%;
      padding: 1.5rem;
      background: #f8fafc;
      overflow: hidden;
    }

    .list-view-period-bar {
      margin-top: 0.5rem;
      margin-bottom: 1.5rem;
      align-self: center;
      width: 100%;
      max-width: 480px;
    }

    .list-table-card {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-height: 0;
      background: white;
      border: 1px solid #e2e8f0;
      border-radius: 16px;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
      overflow: hidden;
    }

    .list-table-card table {
      width: 100%;
      border-collapse: collapse;
    }

    .list-table-card th {
      position: sticky;
      top: 0;
      z-index: 10;
      background: #f8fafc;
      border-bottom: 1px solid #e2e8f0;
      color: #64748b;
      font-weight: 700;
      text-transform: uppercase;
      font-size: 0.75rem;
      letter-spacing: 0.05em;
      padding: 1rem 1.5rem;
    }

    .list-table-card td {
      padding: 1.25rem 1.5rem;
      border-bottom: 1px solid #f1f5f9;
      color: #334155;
      font-size: 0.875rem;
    }

    .list-table-card tr:last-child td {
      border-bottom: none;
    }

    .list-table-card tbody tr {
      transition: background-color 0.2s ease;
    }

    .list-table-card tbody tr:hover {
      background-color: #f8fafc;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AvailabilityComponent implements OnInit {
  private readonly schoolLocationService = inject(SchoolLocationService);
  private readonly moduleVariantService = inject(ModuleVariantService);
  private readonly schoolService = inject(SchoolService);
  private readonly calendarService = inject(CalendarService);
  private readonly lecturerAvailabilityService = inject(LecturerAvailabilityService);
  private readonly contactService = inject(ContactService);
  private readonly authService = inject(AuthService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly dialog = inject(MatDialog);

  // Expose enums for template
  readonly ViewMode = ViewMode;

  // Locale settings
  locale: string = 'de';
  weekStartsOn: 1 = 1; // Monday

  // Signals
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);

  // Current user/contact
  private contactId: number | null = null;

  // Schools
  readonly schools = signal<SchoolWithNestedDetails[]>([]);
  selectedSchoolId: number | null = null;

  // School Locations
  readonly availableLocations = signal<SchoolLocationRead[]>([]);
  readonly selectedLocations = signal<SchoolLocationRead[]>([]);

  // Module Variants
  readonly availableVariants = signal<ModuleVariantWithSlots[]>([]);
  readonly selectedVariants = signal<ModuleVariantWithSlots[]>([]);

  // Calendar state
  readonly viewDate = signal<Date>(new Date());
  readonly viewMode = signal<ViewMode>(ViewMode.Quarter);

  // Calendar events
  readonly calendarEvents = signal<AvailabilityEvent[]>([]);

  // Planned blocks from backend
  readonly plannedBlocks = signal<PlannedBlock[]>([]);
  readonly isListView = signal<boolean>(false);
  readonly sortedPlannedBlocks = computed(() => {
    const events = this.calendarEvents();
    const list: any[] = [];

    events.forEach(event => {
      if (event.isHoliday) return;

      const currentStatus = event.availabilityStatus;
      const originalStatus = event.originalAvailabilityStatus;

      // Check if explicitly entered as available or unavailable by teacher
      const isEntered = 
        currentStatus === AvailabilityStatus.Available || 
        currentStatus === AvailabilityStatus.Unavailable ||
        originalStatus === AvailabilityStatus.Available ||
        originalStatus === AvailabilityStatus.Unavailable;

      if (isEntered) {
        const prefStatus = (currentStatus === AvailabilityStatus.Available || originalStatus === AvailabilityStatus.Available)
          ? 'available'
          : 'unavailable';

        list.push({
          date: format(event.start, 'yyyy-MM-dd'),
          begin_time: event.slots?.[0]?.start_time || '08:00:00',
          end_time: event.slots?.[0]?.end_time || '12:00:00',
          module: event.variantName, // Display variant name in the "module" column
          location: this.getLocationNames(event.locationIds),
          status: prefStatus,
          isPlanned: event.isPlanned === true,
          plannedModule: event.meta?.plannedModule || '',
          planningStatus: event.meta?.planningStatus || '',
          lecture_block_id: event.id
        });
      }
    });

    return list.sort((a, b) => {
      const dateCompare = a.date.localeCompare(b.date);
      if (dateCompare !== 0) return dateCompare;
      return a.begin_time.localeCompare(b.begin_time);
    });
  });

  // Holidays cache - can have multiple holidays per date (different locations)
  private holidaysMap = new Map<string, Holiday[]>();

  // Date range for calendar (2 years)
  private startDate: Date = new Date();
  private endDate: Date = new Date();

  // All locations for filtering
  private allLocations: SchoolLocationRead[] = [];

  // All variants for filtering (before location-based filtering)
  private allVariants: ModuleVariantWithSlots[] = [];

  // Map: variant_id -> array of location_ids where this variant is available
  private variantToLocationsMap = new Map<number, number[]>();

  // Store earliest start date from database
  private earliestStartDate: Date | null = null;

  // Variant that should be auto-set after calendar generation (set by dropVariant)
  private variantToAutoSet: ModuleVariantWithSlots | null = null;

  // Computed properties
  readonly hasValidSelection = computed(() => {
    return this.selectedLocations().length > 0 && this.selectedVariants().length > 0;
  });

  readonly currentQuarter = computed(() => {
    const school = this.schools().find((s) => s.school_id === this.selectedSchoolId);
    const currentDate = this.viewDate();
    if (!school || !school.planning_quarters) return null;
    return school.planning_quarters.find((pq) => {
      const start = new Date(pq.start_date);
      const end = new Date(pq.end_date);
      return currentDate >= start && currentDate <= end;
    });
  });

  readonly periodTitle = computed(() => {
    const school = this.schools().find((s) => s.school_id === this.selectedSchoolId);
    const mode = this.viewMode();
    const currentDate = this.viewDate();

    if (mode === ViewMode.Month) {
      return format(currentDate, 'MMMM yyyy', { locale: de });
    }

    const q = this.currentQuarter();
    if (school && q) {
      if (mode === ViewMode.Quarter) {
        return `Q${q.quarter} ${q.year}`;
      } else {
        // Semester View
        const qIdx = school.planning_quarters.indexOf(q);
        if (qIdx < school.planning_quarters.length - 1) {
          const nextQ = school.planning_quarters[qIdx + 1];
          return `Q${q.quarter} & Q${nextQ.quarter} ${q.year}`;
        }
        return `Q${q.quarter} ${q.year}`;
      }
    }

    if (this.viewMode() === ViewMode.Quarter) {
      // Calendar-year quarters: Q1=Jan-Mar, Q2=Apr-Jun, Q3=Jul-Sep, Q4=Oct-Dec
      const month = currentDate.getMonth(); // 0-indexed
      const calendarQuarter = Math.floor(month / 3) + 1;
      const year = currentDate.getFullYear();
      return `Q${calendarQuarter} ${year}`;
    } else {
      // Semesters: S1=Jan-Jun, S2=Jul-Dec
      const month = currentDate.getMonth(); // 0-indexed
      const semester = month < 6 ? 1 : 2;
      const year = currentDate.getFullYear();
      return `S${semester} ${year}`;
    }
  });

  readonly visibleMonthOffsets = computed(() => {
    const schoolId = this.selectedSchoolId;
    const school = this.schools().find((s) => s.school_id === schoolId);
    const mode = this.viewMode();
    const currentDate = this.viewDate();

    if (!school || !school.planning_quarters || school.planning_quarters.length === 0) {
      if (mode === ViewMode.Month) return [0];
      if (mode === ViewMode.Quarter) return [0, 1, 2];
      return [0, 1, 2, 3, 4, 5];
    }

    // Find the current quarter
    const quarterIndex = school.planning_quarters.findIndex((q) => {
      const qStart = new Date(q.start_date);
      const qEnd = new Date(q.end_date);
      return currentDate >= qStart && currentDate <= qEnd;
    });

    if (quarterIndex === -1) {
      return [0];
    }

    const currentQuarter = school.planning_quarters[quarterIndex];
    if (mode === ViewMode.Month) return [0];

    const qStart = startOfMonth(new Date(currentQuarter.start_date));
    const qEnd = startOfMonth(new Date(currentQuarter.end_date));
    const monthsInCurrent = differenceInMonths(qEnd, qStart) + 1;
    const currentOffsets = Array.from({ length: monthsInCurrent }, (_, i) => i);

    if (mode === ViewMode.Quarter) return currentOffsets;

    // Semester View: current + next
    if (quarterIndex < school.planning_quarters.length - 1) {
      const nextQuarter = school.planning_quarters[quarterIndex + 1];
      const nStart = startOfMonth(new Date(nextQuarter.start_date));
      const nEnd = startOfMonth(new Date(nextQuarter.end_date));
      const monthsInNext = differenceInMonths(nEnd, nStart) + 1;
      const nextOffsets = Array.from({ length: monthsInNext }, (_, i) => monthsInCurrent + i);
      return [...currentOffsets, ...nextOffsets];
    }

    return currentOffsets;
  });

  readonly periodRange = computed(() => {
    const offsets = this.visibleMonthOffsets();
    const mode = this.viewMode();
    const school = this.schools().find((s) => s.school_id === this.selectedSchoolId);

    if (school && school.planning_quarters && school.planning_quarters.length > 0) {
      const q = this.currentQuarter();
      if (q) {
        const qStart = new Date(q.start_date);
        if (mode === ViewMode.Month) {
          const start = startOfMonth(this.viewDate());
          const end = endOfMonth(this.viewDate());
          return `${format(start, 'dd.MM.yyyy')} - ${format(end, 'dd.MM.yyyy')}`;
        } else if (mode === ViewMode.Quarter) {
          const qEnd = new Date(q.end_date);
          return `${format(qStart, 'dd.MM.yyyy')} - ${format(qEnd, 'dd.MM.yyyy')}`;
        } else if (mode === ViewMode.Semester) {
          const qIdx = school.planning_quarters.indexOf(q);
          if (qIdx < school.planning_quarters.length - 1) {
            const nextQ = school.planning_quarters[qIdx + 1];
            const nextEnd = new Date(nextQ.end_date);
            return `${format(qStart, 'dd.MM.yyyy')} - ${format(nextEnd, 'dd.MM.yyyy')}`;
          }
          const qEnd = new Date(q.end_date);
          return `${format(qStart, 'dd.MM.yyyy')} - ${format(qEnd, 'dd.MM.yyyy')}`;
        }
      }
    }

    if (offsets.length === 0) {
      const start = startOfMonth(this.viewDate());
      const end = endOfMonth(this.viewDate());
      return `${format(start, 'dd.MM.yyyy')} - ${format(end, 'dd.MM.yyyy')}`;
    }

    const start = startOfMonth(this.viewDate());
    const end = endOfMonth(addMonths(this.viewDate(), offsets[offsets.length - 1]));
    return `${format(start, 'dd.MM.yyyy')} - ${format(end, 'dd.MM.yyyy')}`;
  });

  readonly planningPeriodRange = computed(() => {
    const school = this.schools().find((s) => s.school_id === this.selectedSchoolId);
    if (!school || !school.planning_quarters || school.planning_quarters.length === 0) {
      return '';
    }
    
    const quarters = school.planning_quarters;
    const start = new Date(quarters[0].start_date);
    const end = new Date(quarters[quarters.length - 1].end_date);
    
    return `${format(start, 'dd.MM.yyyy')} - ${format(end, 'dd.MM.yyyy')}`;
  });

  // Helper function for template
  addMonths = addMonths;

  ngOnInit(): void {
    // Get user email and load contact
    const userInfo = this.authService.getUserInfo();
    if (!userInfo) {
      this.error.set('Benutzerinformationen nicht gefunden.');
      this.loading.set(false);
      return;
    }

    const userEmail = userInfo.sub || userInfo.email;
    if (!userEmail) {
      this.error.set('E-Mail-Adresse nicht gefunden.');
      this.loading.set(false);
      return;
    }

    // Load contact by email
    this.contactService.getContactByEmail(userEmail).subscribe({
      next: (contact) => {
        if (!contact || !contact.contact_id) {
          this.error.set('Kontakt nicht gefunden.');
          this.loading.set(false);
          return;
        }

        this.contactId = contact.contact_id;
        this.loadData(contact.contact_id);
      },
      error: (err) => {
        this.error.set('Fehler beim Laden des Kontakts.');
        this.loading.set(false);
        console.error('Failed to load contact:', err);
      },
    });
  }

  /**
   * Load all data from APIs
   */
  private loadData(contactId: number): void {
    this.loading.set(true);

    // Use the new endpoint based on degree_run_postings instead of module_run_templates
    this.lecturerAvailabilityService
      .getLecturerAvailabilityFiltersDegreePostings(contactId)
      .subscribe({
        next: (filters) => {
          this.schools.set(filters.schools);

          // Populate flat lists from nested structure for backward compatibility and internal use
          this.allLocations = [];
          const variantsMap = new Map<number, ModuleVariantWithSlots>();

          filters.schools.forEach(school => {
            school.locations.forEach(location => {
              // Ensure we have the school mapping in the flat location object
              this.allLocations.push({
                ...location,
                fk_school_id: school.school_id,
                school_shortname: school.school_shortname
              } as any);

              location.variants.forEach(variant => {
                if (!variantsMap.has(variant.module_variant_id)) {
                  variantsMap.set(variant.module_variant_id, {
                    ...variant,
                    degree_ids: variant.degree_ids ? [...variant.degree_ids] : (variant.fk_degree_id ? [variant.fk_degree_id] : []),
                  });
                } else {
                  const existing = variantsMap.get(variant.module_variant_id)!;
                  const mergedDegrees = new Set([
                    ...(existing.degree_ids || []),
                    ...(variant.degree_ids || []),
                    ...(variant.fk_degree_id ? [variant.fk_degree_id] : []),
                  ]);
                  existing.degree_ids = Array.from(mergedDegrees);
                  if (!existing.degree_codes && variant.degree_codes) {
                    existing.degree_codes = variant.degree_codes;
                  }
                  if (!existing.degree_names && variant.degree_names) {
                    existing.degree_names = variant.degree_names;
                  }
                }
              });
            });
          });

          this.availableLocations.set(this.allLocations);
          this.allVariants = Array.from(variantsMap.values());

          // Don't show variants until at least one location is selected
          this.availableVariants.set([]);

          // Store earliest start date from database
          if (filters.earliest_start_date) {
            this.earliestStartDate = new Date(filters.earliest_start_date);
          } else {
            this.earliestStartDate = null;
          }

          // Set data range: Today to Today + 3 years
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          this.startDate = today;
          this.endDate = addMonths(today, 36);

          // Set initial viewDate and range based on school planning periods
          if (filters.schools.length > 0) {
            const initialSchool = filters.schools[0];
            if (initialSchool.planning_quarters && initialSchool.planning_quarters.length > 0) {
              const firstQ = initialSchool.planning_quarters[0];
              this.viewDate.set(new Date(firstQ.start_date));
              this.startDate = new Date(firstQ.start_date);
              
              const lastQ = initialSchool.planning_quarters[initialSchool.planning_quarters.length - 1];
              this.endDate = new Date(lastQ.end_date);
            } else {
              const planningThreshold = this.calculateStartDate(9);
              this.viewDate.set(this.earliestStartDate || planningThreshold);
              this.startDate = new Date();
              this.endDate = addMonths(this.startDate, 36);
            }
          }

          // Auto-select first school
          if (filters.schools.length > 0) {
            this.selectedSchoolId = filters.schools[0].school_id;
            // Note: onSchoolChange used to call generateCalendar, which is fine
            this.onSchoolChange();
            this.loadSavedPreferences(filters, filters.schools[0].school_id);
            this.loadPlannedBlocks(contactId);
          }

          this.loading.set(false);
        },
        error: (err) => {
          this.error.set('Fehler beim Laden der Daten.');
          this.loading.set(false);
          console.error('Failed to load data:', err);
        },
      });
  }

  /**
   * Load saved preferences
   */
  private loadSavedPreferences(filters: LecturerAvailabilityFilters, schoolId: number): void {
    if (!filters.saved_preferences || !filters.saved_preferences[schoolId]) {
      return;
    }

    const prefs = filters.saved_preferences[schoolId];
    const selectedLocationIds = prefs.selected_location_ids || [];
    const selectedVariantIds = prefs.selected_variant_ids || [];

    // Move locations
    if (selectedLocationIds.length > 0) {
      const availableLocs = this.availableLocations();
      const selected: SchoolLocationRead[] = [];
      const remaining: SchoolLocationRead[] = [];

      availableLocs.forEach((loc) => {
        if (selectedLocationIds.includes(loc.location_id)) {
          selected.push(loc);
        } else {
          remaining.push(loc);
        }
      });

      selected.sort(
        (a, b) =>
          selectedLocationIds.indexOf(a.location_id) - selectedLocationIds.indexOf(b.location_id),
      );

      this.availableLocations.set(remaining);
      this.selectedLocations.set(selected);

      // IMPORTANT: Filter variants based on selected locations
      // Now using pre-loaded nested data instead of making fresh backend calls
      const variantLocationMap = new Map<number, Set<number>>();
      const allVariantIds = new Set<number>();

      // Find the school in the filters
      const school = filters.schools.find(s => s.school_id === schoolId);
      if (school) {
        selected.forEach(loc => {
          const nestedLoc = school.locations.find(nl => nl.location_id === loc.location_id);
          if (nestedLoc) {
            nestedLoc.variants.forEach(variant => {
              if (!variantLocationMap.has(variant.module_variant_id)) {
                variantLocationMap.set(variant.module_variant_id, new Set());
              }
              variantLocationMap.get(variant.module_variant_id)!.add(loc.location_id);
              allVariantIds.add(variant.module_variant_id);
            });
          }
        });
      }

      // Convert to Map<number, number[]> for easier use
      this.variantToLocationsMap.clear();
      variantLocationMap.forEach((locationSet, variantId) => {
        this.variantToLocationsMap.set(variantId, Array.from(locationSet));
      });

      // Filter allVariants to only include variants available at any selected location
      const filteredVariants = this.allVariants.filter((v) =>
        allVariantIds.has(v.module_variant_id),
      );

      // Now move variants from saved preferences
      if (selectedVariantIds.length > 0) {
        const selectedVars: ModuleVariantWithSlots[] = [];
        const remainingVars: ModuleVariantWithSlots[] = [];

        filteredVariants.forEach((variant) => {
          if (selectedVariantIds.includes(variant.module_variant_id)) {
            selectedVars.push(variant);
          } else {
            remainingVars.push(variant);
          }
        });

        selectedVars.sort(
          (a, b) =>
            selectedVariantIds.indexOf(a.module_variant_id) -
            selectedVariantIds.indexOf(b.module_variant_id),
        );

        this.availableVariants.set(remainingVars);
        this.selectedVariants.set(selectedVars);
      } else {
        // No variants in saved preferences, just set the filtered variants as available
        this.availableVariants.set(filteredVariants);
        this.selectedVariants.set([]);
      }

      // Generate calendar if selections exist
      if (selectedLocationIds.length > 0 && selectedVariantIds.length > 0) {
        this.generateCalendar();
      }
    }
  }

  /**
   * Handle school change
   */
  /**
   * Handle school change
   */
  onSchoolChange(): void {
    const school = this.schools().find((s: SchoolWithNestedDetails) => s.school_id === this.selectedSchoolId);
    if (!school) return;

    // Use nested locations for the selected school
    const schoolLocations = school.locations.map((loc: LocationWithVariants) => ({
      ...loc,
      fk_school_id: school.school_id,
      school_shortname: school.school_shortname
    } as any));

    this.availableLocations.set(schoolLocations);
    this.selectedLocations.set([]);

    // Don't show variants until at least one location is selected
    this.availableVariants.set([]);
    this.selectedVariants.set([]);

    // Clear the variant-to-locations mapping
    this.variantToLocationsMap.clear();

    // Update view date to start of first planning quarter for this school
    if (school.planning_quarters && school.planning_quarters.length > 0) {
      const firstQ = school.planning_quarters[0];
      this.viewDate.set(new Date(firstQ.start_date));
      this.startDate = new Date(firstQ.start_date);
      const lastQ = school.planning_quarters[school.planning_quarters.length - 1];
      this.endDate = new Date(lastQ.end_date);
    }

    this.generateCalendar();
  }

  /**
   * Handle location drag and drop
   */
  dropLocation(event: CdkDragDrop<SchoolLocationRead[]>): void {
    if (event.previousContainer === event.container) {
      const list = [...event.container.data];
      moveItemInArray(list, event.previousIndex, event.currentIndex);

      if (event.container.data === this.availableLocations()) {
        this.availableLocations.set(list);
      } else {
        this.selectedLocations.set(list);
      }
    } else {
      const previousList = [...event.previousContainer.data];
      const currentList = [...event.container.data];

      transferArrayItem(previousList, currentList, event.previousIndex, event.currentIndex);

      const fromAvailable = event.previousContainer.data === this.availableLocations();

      if (fromAvailable) {
        this.availableLocations.set(previousList);
        this.selectedLocations.set(currentList);
      } else {
        this.selectedLocations.set(previousList);
        this.availableLocations.set(currentList);
      }

      // Filter variants based on selected locations
      this.filterVariantsBySelectedLocations();

      this.generateCalendar();
    }

    this.savePreferences();
  }

  /**
   * Handle variant drag and drop
   */
  dropVariant(event: CdkDragDrop<ModuleVariantWithSlots[]>): void {
    let movedVariant: ModuleVariantWithSlots | null = null;
    let wasMovedToSelected = false;

    if (event.previousContainer === event.container) {
      const list = [...event.container.data];
      moveItemInArray(list, event.previousIndex, event.currentIndex);

      if (event.container.data === this.availableVariants()) {
        this.availableVariants.set(list);
      } else {
        this.selectedVariants.set(list);
      }
    } else {
      const previousList = [...event.previousContainer.data];
      const currentList = [...event.container.data];

      // Capture the moved variant BEFORE transferring
      movedVariant = previousList[event.previousIndex];

      transferArrayItem(previousList, currentList, event.previousIndex, event.currentIndex);

      const fromAvailable = event.previousContainer.data === this.availableVariants();

      if (fromAvailable) {
        this.availableVariants.set(previousList);
        this.selectedVariants.set(currentList);
        wasMovedToSelected = true;
      } else {
        this.selectedVariants.set(previousList);
        this.availableVariants.set(currentList);
        wasMovedToSelected = false;
      }

      // Set flag to auto-set the new variant after calendar generation and loading saved availability
      if (wasMovedToSelected && movedVariant) {
        this.variantToAutoSet = movedVariant;
      }

      this.generateCalendar();

      // Note: autoSetAvailabilityForNewVariant will be called from generateCalendar()
      // after events are generated, not here with setTimeout
    }

    this.savePreferences();
  }

  /**
   * Save preferences to backend
   */
  private savePreferences(): void {
    if (!this.contactId || !this.selectedSchoolId) return;

    const locationIds = this.selectedLocations().map((loc) => loc.location_id);
    const variantIds = this.selectedVariants().map((v) => v.module_variant_id);

    this.lecturerAvailabilityService
      .savePreferences(this.contactId, this.selectedSchoolId, locationIds, variantIds)
      .subscribe({
        next: () => { },
        error: (err) => console.error('Failed to save preferences:', err),
      });
  }

  /**
   * Filter available variants based on selected locations
   * Show all variants that are available at ANY of the selected locations (UNION)
   * Also maintain a map of which variants are available at which locations
   */
  /**
   * Filter available variants based on selected locations
   * Show all variants that are available at ANY of the selected locations (UNION)
   * Also maintain a map of which variants are available at which locations
   */
  private filterVariantsBySelectedLocations(): void {
    const selectedLocs = this.selectedLocations();

    // If no locations selected, don't show any variants
    if (selectedLocs.length === 0) {
      this.availableVariants.set([]);
      this.selectedVariants.set([]);
      this.variantToLocationsMap.clear();
      return;
    }

    // Build a map: variant_id -> Set of location_ids where it's available
    const variantLocationMap = new Map<number, Set<number>>();
    const allVariantIds = new Set<number>();

    // Use pre-loaded nested data for filtering instead of backend calls
    // This is faster and ensures 100% accurate linkage
    const school = this.schools().find((s: SchoolWithNestedDetails) => s.school_id === this.selectedSchoolId);
    if (school) {
      selectedLocs.forEach(loc => {
        const nestedLoc = school.locations.find((nl: LocationWithVariants) => nl.location_id === loc.location_id);
        if (nestedLoc) {
          nestedLoc.variants.forEach((variant: ModuleVariantWithSlots) => {
            if (!variantLocationMap.has(variant.module_variant_id)) {
              variantLocationMap.set(variant.module_variant_id, new Set());
            }
            variantLocationMap.get(variant.module_variant_id)!.add(loc.location_id);
            allVariantIds.add(variant.module_variant_id);
          });
        }
      });
    }

    // Convert to Map<number, number[]> for easier use
    this.variantToLocationsMap.clear();
    variantLocationMap.forEach((locationSet, variantId) => {
      this.variantToLocationsMap.set(variantId, Array.from(locationSet));
    });

    // Filter allVariants to only include variants available at any selected location
    const filteredVariants = this.allVariants.filter((v) =>
      allVariantIds.has(v.module_variant_id),
    );

    // Remove selected variants that are no longer available at any selected location
    const currentSelectedVariants = this.selectedVariants();
    const stillValidSelectedVariants = currentSelectedVariants.filter((v) =>
      allVariantIds.has(v.module_variant_id),
    );

    // Update available variants (exclude currently selected ones that are still valid)
    const stillValidSelectedIds = new Set(
      stillValidSelectedVariants.map((v) => v.module_variant_id),
    );
    const availableOnly = filteredVariants.filter(
      (v) => !stillValidSelectedIds.has(v.module_variant_id),
    );

    this.availableVariants.set(availableOnly);
    this.selectedVariants.set(stillValidSelectedVariants);

    // Regenerate calendar with updated selections
    this.generateCalendar();
  }

  /**
   * Generate calendar events
   */
  private generateCalendar(): void {
    if (!this.hasValidSelection()) {
      this.calendarEvents.set([]);
      return;
    }

    // Load holidays first, then generate events and load saved availability
    this.loadHolidays().then(() => {
      const events: AvailabilityEvent[] = [];
      const selectedLocs = this.selectedLocations();
      const selectedVars = this.selectedVariants();

      // Generate events for all days between startDate and endDate
      let currentDate = new Date(this.startDate);
      while (currentDate <= this.endDate) {
        const dayOfWeek = currentDate.getDay() === 0 ? 7 : currentDate.getDay();
        const dateISO = format(currentDate, 'yyyy-MM-dd');

        // Get all holidays for this date (can be multiple: global + regional for different locations)
        const holidaysForDate = this.holidaysMap.get(dateISO) || [];

        // Check if there's a public holiday on this date
        const publicHoliday = holidaysForDate.find((h) => h.calendar_entry_type === 'public' && h.closed);

        // Check if any degree has an open exception on this date (closed === false)
        const openDegreeExceptions = holidaysForDate.filter(
          (h) => (h.calendar_entry_type === 'degree' || h.fk_degree_id != null) && !h.closed,
        );

        // Helper to check if a variant is exempted from holidays on this date
        const isVariantOpenException = (variant: any): boolean => {
          if (openDegreeExceptions.length === 0) return false;
          const varDegreeId = variant.fk_degree_id;
          const varDegreeIds: number[] = variant.degree_ids || [];
          return openDegreeExceptions.some(
            (ex) =>
              (varDegreeId != null && ex.fk_degree_id === varDegreeId) ||
              varDegreeIds.includes(ex.fk_degree_id!),
          );
        };

        // If public holiday exists and NO selected variants have open exceptions, create only ONE badge
        const hasOpenVariantsOnPublicHoliday =
          publicHoliday && selectedVars.some((v) => isVariantOpenException(v));

        if (publicHoliday && !hasOpenVariantsOnPublicHoliday) {
          const eventId = `public-holiday-${dateISO}`;
          events.push({
            id: eventId,
            start: new Date(currentDate),
            end: new Date(currentDate),
            title: publicHoliday.description || 'Feiertag',
            color: { primary: '#fff9c4', secondary: '#fff59d' }, // Yellow
            availabilityStatus: AvailabilityStatus.PublicHoliday,
            variantId: 0,
            variantName: '',
            slotId: eventId,
            locationIds: selectedLocs.map((loc) => loc.location_id),
            isHoliday: true,
            holidayDescription: publicHoliday.description || undefined,
            holidayType: 'public',
            slots: [],
            meta: {
              availabilityStatus: AvailabilityStatus.PublicHoliday,
              variantId: 0,
              variantName: '',
              slotId: eventId,
              isHoliday: true,
              holidayDescription: publicHoliday.description,
              slots: [],
            },
          });
        } else {
          // Either no public holiday, or some selected variants are exempted via degree open exceptions
          selectedVars.forEach((variant) => {
            // If there's a public holiday and this variant is NOT exempted, render a public holiday badge for it
            if (publicHoliday && !isVariantOpenException(variant)) {
              const eventId = `public-holiday-${variant.module_variant_id}-${dateISO}`;
              events.push({
                id: eventId,
                start: new Date(currentDate),
                end: new Date(currentDate),
                title: `${variant.variant_name} - ${publicHoliday.description || 'Feiertag'}`,
                color: { primary: '#fff9c4', secondary: '#fff59d' },
                availabilityStatus: AvailabilityStatus.PublicHoliday,
                variantId: variant.module_variant_id,
                variantName: variant.variant_name,
                slotId: eventId,
                locationIds: selectedLocs.map((loc) => loc.location_id),
                isHoliday: true,
                holidayDescription: publicHoliday.description || undefined,
                holidayType: 'public',
                slots: [],
                meta: {
                  availabilityStatus: AvailabilityStatus.PublicHoliday,
                  variantId: variant.module_variant_id,
                  variantName: variant.variant_name,
                  slotId: eventId,
                  isHoliday: true,
                  holidayDescription: publicHoliday.description,
                  slots: [],
                },
              });
              return;
            }

            // Get the locations where this variant is available (from our mapping)
            const availableLocationIdsForVariant =
              this.variantToLocationsMap.get(variant.module_variant_id) || [];

            // Filter selected locations to only include those where this variant is available
            const validLocationsForVariant = selectedLocs.filter((loc) =>
              availableLocationIdsForVariant.includes(loc.location_id),
            );

            // Skip this variant if it's not available at any of the selected locations
            if (validLocationsForVariant.length === 0) {
              return;
            }

            if (variant.slots && variant.slots.length > 0) {
              // Check if this variant has slots for this day of week
              const slotsForDay = variant.slots.filter(
                (slot: any) => slot.day_of_week === dayOfWeek,
              );

              if (slotsForDay.length > 0) {
                // Check which valid locations for this variant have a regional holiday
                // If this variant has an open degree exception, it overrides regional/location holidays
                const locationsWithRegionalHoliday = isVariantOpenException(variant)
                  ? []
                  : validLocationsForVariant.filter((loc) =>
                      holidaysForDate.some(
                        (h) =>
                          h.calendar_entry_type === 'region' && h.fk_location_id === loc.location_id,
                      ),
                    );

                const locationsWithoutRegionalHoliday = validLocationsForVariant.filter(
                  (loc) => !locationsWithRegionalHoliday.includes(loc),
                );

                const hasPartialRegionalHoliday =
                  locationsWithRegionalHoliday.length > 0 &&
                  locationsWithoutRegionalHoliday.length > 0;

                if (hasPartialRegionalHoliday) {
                  // CASE 1: Partial regional holiday
                  // - Create separate badges for locations WITH holiday (with location name)
                  // - Create separate badges for each slot for locations WITHOUT holiday

                  // Create holiday badges for locations WITH regional holiday
                  // ONE badge per slot for each location with regional holiday
                  locationsWithRegionalHoliday.forEach((location) => {
                    const regionalHoliday = holidaysForDate.find(
                      (h) =>
                        h.calendar_entry_type === 'region' &&
                        h.fk_location_id === location.location_id,
                    );

                    // Create ONE event per slot for regional holiday locations
                    slotsForDay.forEach((slot: any) => {
                      const timeRange = this.formatTimeRange(slot.start_time, slot.end_time);
                      const slotId = `${slot.module_variant_slot_id || `${slot.start_time}-${slot.end_time}`}`;
                      const eventId = `${variant.module_variant_id}-${location.location_id}-${dateISO}-${slotId}`;

                      // Build title with location info for clarity
                      const eventTitle = `${location.location_shortname} - ${timeRange}`;

                      events.push({
                        id: eventId,
                        start: new Date(currentDate),
                        end: new Date(currentDate),
                        title: eventTitle,
                        color: { primary: '#ffe0b2', secondary: '#ffcc80' }, // Orange for regional
                        availabilityStatus: AvailabilityStatus.RegionalHoliday,
                        variantId: variant.module_variant_id,
                        variantName: variant.variant_name,
                        slotId: eventId,
                        locationIds: [location.location_id],
                        isHoliday: true,
                        holidayDescription: regionalHoliday?.description || undefined,
                        holidayType: 'region',
                        slots: [slot], // Only this specific slot
                        meta: {
                          availabilityStatus: AvailabilityStatus.RegionalHoliday,
                          variantId: variant.module_variant_id,
                          variantName: variant.variant_name,
                          slotId: eventId,
                          isHoliday: true,
                          holidayDescription: regionalHoliday?.description,
                          slots: [slot],
                          locationShortname: location.location_shortname,
                        },
                      });
                    });
                  });

                  // Create separate badges for each slot for locations WITHOUT regional holiday
                  if (locationsWithoutRegionalHoliday.length > 0) {
                    slotsForDay.forEach((slot: any) => {
                      const timeRange = this.formatTimeRange(slot.start_time, slot.end_time);
                      const slotId = `${slot.module_variant_slot_id || `${slot.start_time}-${slot.end_time}`}`;
                      const eventId = `${variant.module_variant_id}-${dateISO}-${slotId}`;

                      // If this variant is only available at a subset of selected locations,
                      // include location info
                      const isPartialLocation =
                        validLocationsForVariant.length < selectedLocs.length;
                      const locationInfo = isPartialLocation
                        ? locationsWithoutRegionalHoliday
                          .map((loc) => loc.location_shortname)
                          .join(', ')
                        : undefined;

                      events.push({
                        id: eventId,
                        start: new Date(currentDate),
                        end: new Date(currentDate),
                        title: timeRange,
                        color: { primary: '#9e9e9e', secondary: '#e0e0e0' }, // Gray for normal
                        availabilityStatus: AvailabilityStatus.Normal,
                        variantId: variant.module_variant_id,
                        variantName: variant.variant_name,
                        slotId: eventId,
                        locationIds: locationsWithoutRegionalHoliday.map((loc) => loc.location_id),
                        isHoliday: false,
                        holidayDescription: undefined,
                        holidayType: undefined,
                        slots: [slot], // Only this specific slot
                        meta: {
                          availabilityStatus: AvailabilityStatus.Normal,
                          variantId: variant.module_variant_id,
                          variantName: variant.variant_name,
                          slotId: eventId,
                          isHoliday: false,
                          holidayDescription: undefined,
                          slots: [slot],
                          locationShortname: locationInfo, // Add location info if partial
                        },
                      });
                    });
                  }
                } else {
                  // CASE 2: No partial regional holiday
                  // Create ONE event per slot (not one event for all slots combined)
                  // This handles:
                  // - All locations have same regional holiday
                  // - No locations have holiday (normal)

                  let holidayStatus = AvailabilityStatus.Normal;
                  let holidayColor = { primary: '#9e9e9e', secondary: '#e0e0e0' }; // Gray default
                  let holidayType: 'region' | undefined;
                  let holidayDescription: string | undefined;

                  if (locationsWithRegionalHoliday.length === validLocationsForVariant.length) {
                    // All valid locations for this variant have a regional holiday
                    holidayStatus = AvailabilityStatus.RegionalHoliday;
                    holidayColor = { primary: '#ffe0b2', secondary: '#ffcc80' }; // Orange
                    holidayType = 'region';
                    const firstRegionalHoliday = holidaysForDate.find(
                      (h) => h.calendar_entry_type === 'region',
                    );
                    holidayDescription = firstRegionalHoliday?.description || undefined;
                  }
                  // Otherwise: Normal (no holiday)

                  // Create ONE event per slot
                  slotsForDay.forEach((slot: any) => {
                    const timeRange = this.formatTimeRange(slot.start_time, slot.end_time);
                    const slotId = `${slot.module_variant_slot_id || `${slot.start_time}-${slot.end_time}`}`;
                    const eventId = `${variant.module_variant_id}-${dateISO}-${slotId}`;

                    // If this variant is only available at a subset of selected locations,
                    // include location info in the title and meta
                    const isPartialLocation = validLocationsForVariant.length < selectedLocs.length;
                    const locationInfo = isPartialLocation
                      ? validLocationsForVariant.map((loc) => loc.location_shortname).join(', ')
                      : undefined;

                    events.push({
                      id: eventId,
                      start: new Date(currentDate),
                      end: new Date(currentDate),
                      title: timeRange,
                      color: holidayColor,
                      availabilityStatus: holidayStatus,
                      variantId: variant.module_variant_id,
                      variantName: variant.variant_name,
                      slotId: eventId,
                      locationIds: validLocationsForVariant.map((loc) => loc.location_id),
                      isHoliday: holidayStatus === AvailabilityStatus.RegionalHoliday,
                      holidayDescription: holidayDescription,
                      holidayType: holidayType,
                      slots: [slot], // Only this specific slot
                      meta: {
                        availabilityStatus: holidayStatus,
                        variantId: variant.module_variant_id,
                        variantName: variant.variant_name,
                        slotId: eventId,
                        isHoliday: holidayStatus === AvailabilityStatus.RegionalHoliday,
                        holidayDescription: holidayDescription,
                        slots: [slot],
                        locationShortname: locationInfo, // Add location info if partial
                      },
                    });
                  });
                }
              }
            }
          });
        }

        currentDate = addDays(currentDate, 1);
      }

      this.calendarEvents.set(events);

      // ALWAYS load saved availability first to restore ALL states (green AND red)
      this.loadSavedAvailability()
        .then(() => {
          // After loading saved availability, check if we need to auto-set a newly added variant
          if (this.variantToAutoSet) {
            const variantToSet = this.variantToAutoSet;
            this.variantToAutoSet = null; // Clear before proceeding

            // Auto-set the newly added variant (only affects gray/Normal events of the new variant)
            this.autoSetAvailabilityForNewVariant(variantToSet);
          }
          
          // Inject planned blocks LAST to override availability states
          this.injectPlannedBlocks();
        })
        .catch((err) => {
          console.error('Error loading saved availability:', err);
          
          // Inject planned blocks even if loading failed
          this.injectPlannedBlocks();

          // Continue with auto-set even if loading failed
          if (this.variantToAutoSet) {
            const variantToSet = this.variantToAutoSet;
            this.variantToAutoSet = null;
            this.autoSetAvailabilityForNewVariant(variantToSet);
          }
        });
    });
  }

  /**
   * Load saved availability from backend and update calendar events
   * Handles 3 states:
   * - Available (Green): is_available=TRUE with date
   * - Unavailable (Red): is_available=FALSE with date
   * - Normal (Gray): No DB entry
   *
   * @returns Promise that resolves when loading is complete
   */
  private loadSavedAvailability(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.contactId || !this.selectedSchoolId) {
        console.log('Cannot load saved availability: missing contact_id or school_id');
        resolve();
        return;
      }

      const startDateStr = format(this.startDate, 'yyyy-MM-dd');
      const endDateStr = format(this.endDate, 'yyyy-MM-dd');

      this.lecturerAvailabilityService
        .getDailyAvailability(this.contactId, this.selectedSchoolId, startDateStr, endDateStr)
        .subscribe({
          next: (response) => {
            // Update calendar events based on saved availability
            const currentEvents = this.calendarEvents();

            const updatedEvents = currentEvents.map((event) => {
              if (event.isHoliday) return event; // Don't change holidays

              const eventDate = format(event.start, 'yyyy-MM-dd');

              // Check if there are any entries for this date
              const availabilityForDate = response.availability_by_date?.[eventDate];
              if (availabilityForDate && availabilityForDate.length > 0) {
                // Get the slot_id from this event (if it has one)
                const eventSlotId =
                  event.slots && event.slots.length > 0
                    ? event.slots[0].module_variant_slot_id
                    : undefined;

                // Check if available (green) - match variant, location AND slot_id
                const isAvailable = availabilityForDate.some(
                  (avail) =>
                    avail.variant_id === event.variantId &&
                    event.locationIds.includes(avail.location_id) &&
                    avail.status === 'available' &&
                    (avail.slot_id === eventSlotId ||
                      (avail.slot_id === null && eventSlotId === undefined)),
                );

                if (isAvailable) {
                  return {
                    ...event,
                    availabilityStatus: AvailabilityStatus.Available,
                    color: { primary: '#4caf50', secondary: '#c8e6c9' },
                    meta: {
                      ...event.meta,
                      availabilityStatus: AvailabilityStatus.Available,
                    },
                  };
                }

                // Check if unavailable (red) - match variant, location AND slot_id
                const isUnavailable = availabilityForDate.some(
                  (avail) =>
                    avail.variant_id === event.variantId &&
                    event.locationIds.includes(avail.location_id) &&
                    avail.status === 'unavailable' &&
                    (avail.slot_id === eventSlotId ||
                      (avail.slot_id === null && eventSlotId === undefined)),
                );

                if (isUnavailable) {
                  return {
                    ...event,
                    availabilityStatus: AvailabilityStatus.Unavailable,
                    color: { primary: '#f44336', secondary: '#ffcdd2' },
                    meta: {
                      ...event.meta,
                      availabilityStatus: AvailabilityStatus.Unavailable,
                    },
                  };
                }
              }

              return event; // Keep as Normal (Gray) - no DB entry
            });

            this.calendarEvents.set(updatedEvents);
            resolve(); // Resolve the promise when loading is complete
          },
          error: (err) => {
            console.error('Failed to load saved availability:', err);
            reject(err);
          },
        });
    });
  }

  /**
   * Load holidays
   */
  private async loadHolidays(): Promise<void> {
    if (!this.selectedLocations().length) return;

    const locationIds = this.selectedLocations()
      .map((loc) => loc.location_id)
      .join(',');

    // Use current date as start for holidays (not this.startDate which may be in the future)
    // This ensures we can navigate back and see holidays for earlier months
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const holidayStartDate = today;
    const holidayEndDate = addMonths(today, 30); // 2.5 years from now

    const degreeIds = Array.from(
      new Set(
        this.selectedVariants()
          .flatMap((v) => (v.degree_ids && v.degree_ids.length > 0 ? v.degree_ids : (v.fk_degree_id ? [v.fk_degree_id] : [])))
          .filter((id): id is number => id != null),
      ),
    ).join(',');

    return new Promise((resolve) => {
      this.calendarService
        .getHolidays({
          location_ids: locationIds,
          degree_ids: degreeIds || undefined,
          school_id: this.selectedSchoolId || undefined,
          include_exceptions: true,
          start_date: format(holidayStartDate, 'yyyy-MM-dd'),
          end_date: format(holidayEndDate, 'yyyy-MM-dd'),
        })
        .subscribe({
          next: (holidays) => {
            this.holidaysMap.clear();
            // Group holidays by date (can have multiple holidays per date)
            holidays.forEach((holiday) => {
              const existingHolidays = this.holidaysMap.get(holiday.entry_date) || [];
              existingHolidays.push(holiday);
              this.holidaysMap.set(holiday.entry_date, existingHolidays);
            });
            resolve();
          },
          error: (err) => {
            console.error('Failed to load holidays:', err);
            resolve();
          },
        });
    });
  }

  /**
   * Handle day click - open dialog with all events for that day
   */
  handleDayClick(event: any): void {
    const day = event.day;
    if (!day) {
      return;
    }

    const dialogRef = this.dialog.open(DayCellDetailDialogComponent, {
      width: '600px',
      maxWidth: '90vw',
      maxHeight: '80vh',
      data: {
        date: day.date,
        events: day.events,
        component: this,
      },
    });
  }

  /**
   * Handle event click
   * Cycles through 3 states: Normal (Gray) → Available (Green) → Unavailable (Red) → Normal
   */
  handleEventClick(event: any): void {
    const calEvent = event.event as AvailabilityEvent;

    if (calEvent.isHoliday) {
      this.snackBar.open('Feiertage können nicht bearbeitet werden', 'OK', { duration: 2000 });
      return;
    }

    if ((calEvent as any).isPlanned) {
      this.snackBar.open('Dieser Termin ist bereits fest geplant und kann nicht geändert werden.', 'OK', { duration: 3000 });
      return;
    }

    // Cycle through 2 states: Available ↔ Unavailable
    // Since default is Available, clicking Available makes it Unavailable, and any other state (including Normal) becomes Unavailable
    let newStatus: AvailabilityStatus;
    if (calEvent.availabilityStatus === AvailabilityStatus.Unavailable) {
      newStatus = AvailabilityStatus.Available;
    } else {
      newStatus = AvailabilityStatus.Unavailable;
    }

    // Update UI immediately (optimistic update)
    calEvent.availabilityStatus = newStatus;

    // Set colors based on status
    if (newStatus === AvailabilityStatus.Available) {
      calEvent.color = { primary: '#4caf50', secondary: '#c8e6c9' }; // Green
    } else if (newStatus === AvailabilityStatus.Unavailable) {
      calEvent.color = { primary: '#f44336', secondary: '#ffcdd2' }; // Red
    } else {
      calEvent.color = { primary: '#9e9e9e', secondary: '#e0e0e0' }; // Gray
    }

    if (calEvent.meta) {
      calEvent.meta.availabilityStatus = newStatus;
    }

    // Trigger change detection
    this.calendarEvents.set([...this.calendarEvents()]);

    // Save to backend
    if (!this.contactId || !this.selectedSchoolId) {
      this.snackBar.open('Kontakt-ID oder Schul-ID fehlt', 'OK', { duration: 3000 });
      return;
    }

    const eventDate = format(calEvent.start, 'yyyy-MM-dd');

    // Extract slot_id if available (for slot-level availability)
    const slotId =
      calEvent.slots && calEvent.slots.length > 0
        ? calEvent.slots[0].module_variant_slot_id
        : undefined;

    // For each location and variant combination in this event
    calEvent.locationIds.forEach((locationId) => {
      const request = {
        school_id: this.selectedSchoolId!,
        location_id: locationId,
        variant_id: calEvent.variantId,
        slot_id: slotId, // Include slot_id for slot-level availability
        availability_date: eventDate,
        status: newStatus, // 'normal', 'available', or 'unavailable'
      };

      this.lecturerAvailabilityService.saveDailyAvailability(this.contactId!, request).subscribe({
        next: () => {
          // Success silently
        },
        error: (err) => {
          console.error('Failed to save availability:', err);
          this.snackBar.open('Fehler beim Speichern der Verfügbarkeit', 'OK', { duration: 3000 });
          // Revert UI on error - toggle back
          if (newStatus === AvailabilityStatus.Available) {
            calEvent.availabilityStatus = AvailabilityStatus.Unavailable;
            calEvent.color = { primary: '#f44336', secondary: '#ffcdd2' };
          } else {
            // Note: If it was Normal and changed to Unavailable, and failed, it stays Normal logically, but we map it back.
            // However, to keep it simple and binary:
            calEvent.availabilityStatus = AvailabilityStatus.Available;
            calEvent.color = { primary: '#4caf50', secondary: '#c8e6c9' };
          }
          this.calendarEvents.set([...this.calendarEvents()]);
        },
      });
    });
  }

  /**
   * Handle click on day events badge (for quarter/semester view)
   * Cycles all non-holiday events through 3 states together
   */
  handleDayEventsClick(events: CalendarEvent[]): void {
    const availEvents = events.filter(
      (e) => !(e as AvailabilityEvent).isHoliday && !(e as any).isPlanned,
    ) as AvailabilityEvent[];

    if (availEvents.length === 0) {
      this.snackBar.open('Keine bearbeitbaren Events an diesem Tag', 'OK', { duration: 2000 });
      return;
    }

    // Cycle through 2 states based on current majority: Available (including Normal) ↔ Unavailable
    let nextStatus: AvailabilityStatus;
    let nextColor: { primary: string; secondary: string };

    const availableCount = availEvents.filter(
      (e) => e.availabilityStatus === AvailabilityStatus.Normal || e.availabilityStatus === AvailabilityStatus.Available
    ).length;
    const unavailableCount = availEvents.filter(
      (e) => e.availabilityStatus === AvailabilityStatus.Unavailable
    ).length;

    if (availableCount >= unavailableCount) {
      // Currently mostly available -> make unavailable
      nextStatus = AvailabilityStatus.Unavailable;
      nextColor = { primary: '#f44336', secondary: '#ffcdd2' };
    } else {
      // Currently mostly unavailable -> make available
      nextStatus = AvailabilityStatus.Available;
      nextColor = { primary: '#4caf50', secondary: '#c8e6c9' };
    }

    // Apply status to all non-holiday events
    availEvents.forEach((event) => {
      event.availabilityStatus = nextStatus;
      event.color = nextColor;
      if (event.meta) {
        event.meta.availabilityStatus = nextStatus;
      }
    });

    // Trigger change detection
    this.calendarEvents.set([...this.calendarEvents()]);

    // Save to backend (batch operation would be better but individual calls work too)
    if (!this.contactId || !this.selectedSchoolId) {
      return;
    }

    const eventDate = format(availEvents[0].start, 'yyyy-MM-dd');

    availEvents.forEach((event) => {
      event.locationIds.forEach((locationId) => {
        const request = {
          school_id: this.selectedSchoolId!,
          location_id: locationId,
          variant_id: event.variantId,
          availability_date: eventDate,
          status: nextStatus, // 'normal', 'available', or 'unavailable'
        };

        this.lecturerAvailabilityService.saveDailyAvailability(this.contactId!, request).subscribe({
          error: (err) => {
            console.error('Failed to save availability:', err);
          },
        });
      });
    });
  }

  /**
   * Handle event times changed (drag)
   */
  handleEventTimesChanged(event: CalendarEventTimesChangedEvent): void {
    // Not implemented yet
  }

  /**
   * Calculate the start and end dates of the currently visible period
   * based on database-defined planning quarters
   */
  private getCurrentVisiblePeriodRange(): { start: Date; end: Date } {
    const school = this.schools().find((s) => s.school_id === this.selectedSchoolId);
    const mode = this.viewMode();
    const currentDate = this.viewDate();

    // Default fallbacks
    let periodStart = startOfMonth(currentDate);
    let periodEnd = endOfMonth(currentDate);

    if (mode === ViewMode.Quarter) {
      periodEnd = endOfMonth(addMonths(currentDate, 2));
    } else if (mode === ViewMode.Semester) {
      periodEnd = endOfMonth(addMonths(currentDate, 5));
    }

    if (school && school.planning_quarters && school.planning_quarters.length > 0) {
      const q = this.currentQuarter();
      if (q) {
        periodStart = new Date(q.start_date);
        if (mode === ViewMode.Month) {
          periodStart = startOfMonth(currentDate);
          periodEnd = endOfMonth(currentDate);
        } else if (mode === ViewMode.Quarter) {
          periodEnd = new Date(q.end_date);
        } else if (mode === ViewMode.Semester) {
          const qIdx = school.planning_quarters.indexOf(q);
          if (qIdx < school.planning_quarters.length - 1) {
            periodEnd = new Date(school.planning_quarters[qIdx + 1].end_date);
          } else {
            periodEnd = new Date(q.end_date);
          }
        }
      }
    }

    return { start: periodStart, end: periodEnd };
  }

  /**
   * Auto-set availability to "available" (green) for all events of a newly added variant
   * This is called when a variant is drag-and-dropped from "Verfügbar" to "Meine Varianten"
   * Only sets availability for the currently visible period (Month, Quarter, or Semester)
   *
   * @param variant The module variant that was just added to "Meine Varianten"
   */
  private autoSetAvailabilityForNewVariant(variant: ModuleVariantRead): void {
    if (!this.contactId || !this.selectedSchoolId) {
      console.log('Cannot auto-set availability: missing contact_id or school_id');
      return; 
    }

    const { start: periodStart, end: periodEnd } = this.getCurrentVisiblePeriodRange();

    // Get all calendar events for this variant that are not holidays AND within the current period
    const currentEvents = this.calendarEvents();

    const variantEvents = currentEvents.filter((event) => {
      const eventDate = event.start;
      return (
        event.variantId === variant.module_variant_id &&
        !event.isHoliday &&
        event.availabilityStatus === AvailabilityStatus.Normal && // Only auto-set events that are currently "Normal" (gray)
        eventDate >= periodStart &&
        eventDate <= periodEnd // IMPORTANT: Only events within the current visible period
      );
    });

    if (variantEvents.length === 0) {
      // No need to load saved availability here - it was already loaded before calling this function
      return;
    }

    // Update UI immediately (optimistic update)
    // IMPORTANT: Get the LATEST events from the signal (after loadSavedAvailability has updated them)
    const latestEvents = this.calendarEvents();
    const updatedEvents = latestEvents.map((event) => {
      // Check if this is one of the variant events we want to update
      const shouldUpdate = variantEvents.some((ve) => ve.id === event.id);

      if (shouldUpdate) {
        return {
          ...event,
          availabilityStatus: AvailabilityStatus.Available,
          color: { primary: '#4caf50', secondary: '#c8e6c9' }, // Green
          meta: {
            ...event.meta,
            availabilityStatus: AvailabilityStatus.Available,
          },
        };
      }

      return event;
    });

    // Update calendar UI
    this.calendarEvents.set(updatedEvents);

    // Save all events to backend
    let successCount = 0;
    let errorCount = 0;
    const totalEvents = variantEvents.length;

    // Get period label for success message
    const periodLabel = this.periodTitle();

    // Calculate total number of save requests
    const totalSaveRequests = variantEvents.reduce(
      (sum, event) => sum + event.locationIds.length,
      0,
    );

    variantEvents.forEach((event) => {
      const eventDate = format(event.start, 'yyyy-MM-dd');

      // Extract slot_id if available
      const slotId =
        event.slots && event.slots.length > 0 ? event.slots[0].module_variant_slot_id : undefined;

      // Save for each location
      event.locationIds.forEach((locationId) => {
        const request = {
          school_id: this.selectedSchoolId!,
          location_id: locationId,
          variant_id: event.variantId,
          slot_id: slotId,
          availability_date: eventDate,
          status: 'available' as const,
        };

        this.lecturerAvailabilityService.saveDailyAvailability(this.contactId!, request).subscribe({
          next: () => {
            successCount++;
            // Check if all requests are complete
            if (successCount + errorCount === totalSaveRequests) {
              // All saves are done - events were already set green via optimistic update
              // No success message shown (removed per user request)
            }
          },
          error: (err) => {
            console.error('Failed to auto-save availability:', err);
            errorCount++;
            // Check if all requests are complete
            if (successCount + errorCount === totalSaveRequests) {
              // Show error message only
              if (errorCount > 0) {
                this.snackBar.open(
                  `Fehler beim Speichern: ${errorCount} von ${totalSaveRequests} Terminen konnten nicht gespeichert werden`,
                  'OK',
                  { duration: 4000 },
                );
              }
            }
          },
        });
      });
    });
  }

  /**
   * View mode change handler
   */
  onViewModeChange(newMode: ViewMode): void {
    // Update the viewMode signal
    this.viewMode.set(newMode);

    // DO NOT auto-save additional months - this was causing all gray events to turn green on reload
    // Auto-save should only happen when a NEW variant is added, not on view mode changes

    // Force change detection by creating a new array reference
    // This ensures that the calendar events are re-rendered with correct CSS classes
    // when switching between views (Month, Quarter, Semester)
    this.calendarEvents.set([...this.calendarEvents()]);
  }

  /**
   * Automatically save additional visible months for all selected variants
   * This is called when view mode changes to ensure newly visible months are saved
   */
  private autoSaveAdditionalMonthsForSelectedVariants(): void {
    if (!this.contactId || !this.selectedSchoolId) {
      return;
    }

    // Get all selected variants
    const selectedVars = this.selectedVariants();
    if (selectedVars.length === 0) {
      return;
    }

    // Calculate the start and end dates of the currently visible period
    const { start: periodStart, end: periodEnd } = this.getCurrentVisiblePeriodRange();

    // Get all calendar events within the visible period
    const currentEvents = this.calendarEvents();

    // For each selected variant, find Normal (gray) events within the visible period
    // These are events that haven't been saved yet
    const eventsToSave = currentEvents.filter((event) => {
      const eventDate = event.start;
      const isSelectedVariant = selectedVars.some((v) => v.module_variant_id === event.variantId);

      return (
        isSelectedVariant &&
        !event.isHoliday &&
        event.availabilityStatus === AvailabilityStatus.Normal && // Only auto-save events that are currently "Normal" (gray)
        eventDate >= periodStart &&
        eventDate <= periodEnd
      );
    });

    if (eventsToSave.length === 0) {
      // No unsaved events in the visible period
      return;
    }

    // Update UI immediately (optimistic update)
    const latestEvents = this.calendarEvents();
    const updatedEvents = latestEvents.map((event) => {
      // Check if this is one of the events we want to update
      const shouldUpdate = eventsToSave.some((e) => e.id === event.id);

      if (shouldUpdate) {
        return {
          ...event,
          availabilityStatus: AvailabilityStatus.Available,
          color: { primary: '#4caf50', secondary: '#c8e6c9' }, // Green
          meta: {
            ...event.meta,
            availabilityStatus: AvailabilityStatus.Available,
          },
        };
      }

      return event;
    });

    // Update calendar UI
    this.calendarEvents.set(updatedEvents);

    // Save all events to backend
    eventsToSave.forEach((event) => {
      const eventDate = format(event.start, 'yyyy-MM-dd');

      // Extract slot_id if available
      const slotId =
        event.slots && event.slots.length > 0 ? event.slots[0].module_variant_slot_id : undefined;

      // Save for each location
      event.locationIds.forEach((locationId) => {
        const request = {
          school_id: this.selectedSchoolId!,
          location_id: locationId,
          variant_id: event.variantId,
          slot_id: slotId,
          availability_date: eventDate,
          status: 'available' as const,
        };

        this.lecturerAvailabilityService.saveDailyAvailability(this.contactId!, request).subscribe({
          next: () => {
            // Successfully saved
          },
          error: (err) => {
            console.error('Failed to auto-save additional months:', err);
          },
        });
      });
    });
  }

  /**
   * Navigate to previous period
   */
  previousPeriod(): void {
    const school = this.schools().find((s) => s.school_id === this.selectedSchoolId);
    if (!school || !school.planning_quarters || school.planning_quarters.length === 0) {
      // Legacy fallback logic
      let newDate: Date;
      if (this.viewMode() === ViewMode.Month) {
        newDate = addMonths(this.viewDate(), -1);
      } else if (this.viewMode() === ViewMode.Quarter) {
        newDate = addMonths(this.viewDate(), -3);
      } else {
        newDate = addMonths(this.viewDate(), -6);
      }
      this.viewDate.set(startOfMonth(newDate));
    } else {
      const currentViewDate = this.viewDate();
      const currentQuarterIndex = school.planning_quarters.findIndex((q) => {
        const start = new Date(q.start_date);
        const end = new Date(q.end_date);
        return currentViewDate >= start && currentViewDate <= end;
      });

      if (currentQuarterIndex > 0) {
        const prevQuarter = school.planning_quarters[currentQuarterIndex - 1];
        this.viewDate.set(new Date(prevQuarter.start_date));
      } else {
        // Fallback to current quarter start if already at the first one
        this.viewDate.set(new Date(school.planning_quarters[0].start_date));
      }
    }

    // Auto-save additional visible months for all selected variants
    this.autoSaveAdditionalMonthsForSelectedVariants();

    // Force change detection to ensure event colors are preserved
    this.calendarEvents.set([...this.calendarEvents()]);
  }

  /**
   * Navigate to next period
   */
  nextPeriod(): void {
    const school = this.schools().find((s) => s.school_id === this.selectedSchoolId);
    if (!school || !school.planning_quarters || school.planning_quarters.length === 0) {
      // Legacy fallback logic
      let newDate: Date;
      if (this.viewMode() === ViewMode.Month) {
        newDate = addMonths(this.viewDate(), 1);
      } else if (this.viewMode() === ViewMode.Quarter) {
        newDate = addMonths(this.viewDate(), 3);
      } else {
        newDate = addMonths(this.viewDate(), 6);
      }
      this.viewDate.set(startOfMonth(newDate));
    } else {
      const currentViewDate = this.viewDate();
      const currentQuarterIndex = school.planning_quarters.findIndex((q) => {
        const start = new Date(q.start_date);
        const end = new Date(q.end_date);
        return currentViewDate >= start && currentViewDate <= end;
      });

      if (currentQuarterIndex >= 0 && currentQuarterIndex < school.planning_quarters.length - 1) {
        const nextQuarter = school.planning_quarters[currentQuarterIndex + 1];
        this.viewDate.set(new Date(nextQuarter.start_date));
      }
    }

    // Auto-save additional visible months for all selected variants
    this.autoSaveAdditionalMonthsForSelectedVariants();

    // Force change detection to ensure event colors are preserved
    this.calendarEvents.set([...this.calendarEvents()]);
  }

  /**
   * Can go to previous period
   */
  canGoPrevious(): boolean {
    const school = this.schools().find((s) => s.school_id === this.selectedSchoolId);
    if (!school || !school.planning_quarters || school.planning_quarters.length === 0) {
      return this.viewDate() > this.startDate;
    }

    const currentViewDate = this.viewDate();
    const currentQuarterIndex = school.planning_quarters.findIndex((q) => {
      const start = new Date(q.start_date);
      const end = new Date(q.end_date);
      return currentViewDate >= start && currentViewDate <= end;
    });

    return currentQuarterIndex > 0;
  }

  /**
   * Can go to next period
   */
  canGoNext(): boolean {
    const school = this.schools().find((s) => s.school_id === this.selectedSchoolId);
    if (!school || !school.planning_quarters || school.planning_quarters.length === 0) {
      if (this.viewMode() === ViewMode.Month) {
        return addMonths(this.viewDate(), 1) < this.endDate;
      } else if (this.viewMode() === ViewMode.Quarter) {
        return addMonths(this.viewDate(), 3) < this.endDate;
      } else {
        return addMonths(this.viewDate(), 6) < this.endDate;
      }
    }

    const currentViewDate = this.viewDate();
    const currentQuarterIndex = school.planning_quarters.findIndex((q) => {
      const start = new Date(q.start_date);
      const end = new Date(q.end_date);
      return currentViewDate >= start && currentViewDate <= end;
    });

    return currentQuarterIndex < school.planning_quarters.length - 1;
  }

  /**
   * Get events for specific month offset
   */
  getEventsForMonth(offset: number): AvailabilityEvent[] {
    const targetMonth = addMonths(this.viewDate(), offset);
    const events = this.calendarEvents();

    // Filter events that fall within the target month
    return events.filter((event) => {
      const eventDate = event.start;
      return isSameMonth(eventDate, targetMonth);
    });
  }

  /**
   * Get month name
   */
  getMonthName(date: Date): string {
    return format(date, 'MMMM yyyy', { locale: de });
  }

  /**
   * Get event tooltip
   */
  getEventTooltip(event: CalendarEvent): string {
    const availEvent = event as AvailabilityEvent;

    if (availEvent.isHoliday) {
      return availEvent.holidayDescription || '';
    }

    const statusText =
      availEvent.availabilityStatus === AvailabilityStatus.Available
        ? 'Verfügbar (Grün)'
        : 'Nicht verfügbar (Rot)';

    return `${availEvent.title}\n${availEvent.variantName}\nStatus: ${statusText}\n\nKlicken zum Umschalten`;
  }

  /**
   * Format event date for display (e.g., "21.02.2025")
   */
  formatEventDate(event: CalendarEvent): string {
    return format(event.start, 'dd.MM.yyyy', { locale: de });
  }

  /**
   * Format event time range for display (e.g., "13:30 - 15:30" or "13:00-16:00, 18:15-21:30")
   */
  formatEventTimeRange(event: CalendarEvent): string {
    const availEvent = event as AvailabilityEvent;
    if (availEvent.isHoliday) {
      return '';
    }

    // If we have multiple slots, show them all
    if (availEvent.slots && availEvent.slots.length > 1) {
      return availEvent.slots
        .map((slot) => `${slot.start_time.substring(0, 5)}-${slot.end_time.substring(0, 5)}`)
        .join(', ');
    }

    // Otherwise use the title which already contains the time range(s)
    // title is like "13:00-16:00, 18:15-21:30"
    return availEvent.title?.replace(/-/g, ' - ').replace(/,/g, ',\n') || '';
  }

  /**
   * Format individual slot time for display (e.g., "13:00 - 16:00")
   */
  formatSlotTime(slot: ModuleVariantSlot): string {
    const start = slot.start_time.substring(0, 5);
    const end = slot.end_time.substring(0, 5);
    return `${start} - ${end}`;
  }

  /**
   * Get tooltip for all events in a day
   */
  getDayEventsTooltip(events: CalendarEvent[]): string {
    const availEvents = events as AvailabilityEvent[];
    const lines = availEvents.map((e) => {
      if (e.isHoliday) {
        const holidayTypeText =
          e.holidayType === 'public'
            ? 'Globaler Feiertag'
            : e.holidayType === 'region'
              ? 'Regionaler Feiertag'
              : 'Feiertag';
        return `🎉 ${holidayTypeText}: ${e.holidayDescription}`;
      }
      const status =
        e.availabilityStatus === AvailabilityStatus.Available
          ? '✓'
          : e.availabilityStatus === AvailabilityStatus.Unavailable
            ? '✗'
            : '○';
      return `${status} ${e.title} - ${e.variantName}`;
    });
    return lines.join('\n');
  }

  /**
   * Check if day has available events
   */
  hasAvailableEvents(events: CalendarEvent[]): boolean {
    return events.some(
      (e) => (e as AvailabilityEvent).availabilityStatus === AvailabilityStatus.Available,
    );
  }

  /**
   * Check if day has unavailable events
   */
  hasUnavailableEvents(events: CalendarEvent[]): boolean {
    return events.some(
      (e) => (e as AvailabilityEvent).availabilityStatus === AvailabilityStatus.Unavailable,
    );
  }

  /**
   * Check if day has holiday events (any type)
   */
  hasHolidayEvents(events: CalendarEvent[]): boolean {
    return events.some((e) => (e as AvailabilityEvent).isHoliday);
  }

  /**
   * Check if day has public holiday events
   */
  hasPublicHolidayEvents(events: CalendarEvent[]): boolean {
    return events.some(
      (e) =>
        (e as AvailabilityEvent).availabilityStatus === AvailabilityStatus.PublicHoliday ||
        ((e as AvailabilityEvent).isHoliday && (e as AvailabilityEvent).holidayType === 'public'),
    );
  }

  /**
   * Check if day has regional holiday events
   */
  hasRegionalHolidayEvents(events: CalendarEvent[]): boolean {
    return events.some(
      (e) =>
        (e as AvailabilityEvent).availabilityStatus === AvailabilityStatus.RegionalHoliday ||
        ((e as AvailabilityEvent).isHoliday && (e as AvailabilityEvent).holidayType === 'region'),
    );
  }

  /**
   * Format time range
   */
  private formatTimeRange(startTime: string, endTime: string): string {
    return `${startTime.substring(0, 5)} - ${endTime.substring(0, 5)}`;
  }

  /**
   * Calculate dynamic start date based on database logic:
   * date_trunc('quarter', now()) + interval '9 months'
   */
  private calculateStartDate(monthsAhead: number = 9): Date {
    const now = new Date();
    const quarterStartMonth = Math.floor(now.getMonth() / 3) * 3;
    const quarterStart = new Date(now.getFullYear(), quarterStartMonth, 1);
    quarterStart.setHours(0, 0, 0, 0);
    const startDate = new Date(quarterStart);
    startDate.setMonth(startDate.getMonth() + monthsAhead);
    return startDate;
  }

  /**
   * Calculate the number of months between startDate and a given date
   * Used for custom quarter/semester calculations
   */
  private getMonthsSinceStart(date: Date): number {
    const years = date.getFullYear() - this.startDate.getFullYear();
    const months = date.getMonth() - this.startDate.getMonth();
    return years * 12 + months;
  }

  /**
   * Format variant times
   */
  formatVariantTimes(variant: ModuleVariantRead): string {
    if (!variant.slots || variant.slots.length === 0) return '';

    const timeRanges = variant.slots
      .filter((slot) => slot.start_time && slot.end_time) // Filter out slots with null times
      .map((slot) => `${slot.start_time.substring(0, 5)}-${slot.end_time.substring(0, 5)}`)
      .filter((value, index, self) => self.indexOf(value) === index);

    return timeRanges.join(', ');
  }

  /**
   * Get event meta data (helper for template)
   */
  getEventMeta(event: CalendarEvent): any {
    return (
      event.meta || {
        availabilityStatus: 'normal',
        isHoliday: false,
      }
    );
  }

  /**
   * Cast CalendarEvent to AvailabilityEvent (helper for template)
   */
  getAvailabilityEvent(event: CalendarEvent): AvailabilityEvent {
    return event as AvailabilityEvent;
  }

  /**
   * Helper to map location IDs to shortnames
   */
  getLocationNames(locationIds: number[]): string {
    const locs = this.selectedLocations();
    const names = locationIds
      .map(id => locs.find(l => l.location_id === id)?.location_shortname || '')
      .filter(n => n !== '');
    return names.map(name => name.endsWith('DIG') || name === 'ONLINE' ? 'ONLINE' : name).join(', ');
  }

  /**
   * Load planned blocks from backend
   */
  private loadPlannedBlocks(contactId: number): void {
    this.lecturerAvailabilityService.getPlannedBlocks(contactId).subscribe({
      next: (blocks) => {
        this.plannedBlocks.set(blocks);
        // Only trigger regeneration if we already have a selection
        if (this.hasValidSelection()) {
          this.generateCalendar();
        }
      },
      error: (err) => {
        console.error('Failed to load planned blocks:', err);
      },
    });
  }

  /**
   * Inject planned blocks into calendar events
   */
  private injectPlannedBlocks(): void {
    const currentEvents = this.calendarEvents();
    const planned = this.plannedBlocks();

    if (planned.length === 0) return;

    // Keep track of which planned blocks were successfully matched to existing availability events
    const matchedPlannedIds = new Set<number>();

    const updatedEvents = currentEvents.map((event) => {
      if (event.isHoliday) return event;

      const eventDate = format(event.start, 'yyyy-MM-dd');
      const eventBegin = event.slots?.[0]?.start_time;
      const eventEnd = event.slots?.[0]?.end_time;

      // Find matching planned block
      const matchingBlock = planned.find((b) => {
        return (
          b.date === eventDate &&
          b.begin_time.substring(0, 5) === eventBegin?.substring(0, 5) &&
          b.end_time.substring(0, 5) === eventEnd?.substring(0, 5)
        );
      });

      if (matchingBlock) {
        matchedPlannedIds.add(matchingBlock.lecture_block_id);
        
        const originalStatus = event.availabilityStatus;
        let status = AvailabilityStatus.Normal;
        let color = { primary: '#9e9e9e', secondary: '#e0e0e0' };

        if (matchingBlock.planning_status === 'DRAFT') {
          status = AvailabilityStatus.Draft;
          color = { primary: '#f97316', secondary: '#ffedd5' }; // Orange
        } else if (matchingBlock.planning_status === 'APPROVED') {
          status = AvailabilityStatus.Approved;
          color = { primary: '#a855f7', secondary: '#f3e8ff' }; // Purple
        } else if (matchingBlock.planning_status === 'CONFIRMED') {
          status = AvailabilityStatus.Approved;
          color = matchingBlock.is_guaranteed
            ? { primary: '#4f46e5', secondary: '#e0e7ff' } // Dark Indigo
            : { primary: '#0284c7', secondary: '#e0f2fe' }; // Sky Blue
        }

        return {
          ...event,
          availabilityStatus: status,
          color,
          isPlanned: true,
          planningStatus: matchingBlock.planning_status,
          isGuaranteed: matchingBlock.is_guaranteed,
          originalAvailabilityStatus: originalStatus,
          title: `${event.title} (${matchingBlock.module})`,
          meta: {
            ...event.meta,
            availabilityStatus: status,
            isPlanned: true,
            planningStatus: matchingBlock.planning_status,
            isGuaranteed: matchingBlock.is_guaranteed,
            originalAvailabilityStatus: originalStatus,
            plannedModule: matchingBlock.module,
          },
        } as AvailabilityEvent;
      }

      return event;
    });

    // NOW: Add events for planned blocks that WERE NOT matched 
    // (e.g. because the location/variant was not selected by the teacher)
    const unmatchedBlocks = planned.filter(b => !matchedPlannedIds.has(b.lecture_block_id));
    
    unmatchedBlocks.forEach(block => {
      const status = block.planning_status === 'DRAFT' ? AvailabilityStatus.Draft : AvailabilityStatus.Approved;
      let color = { primary: '#9e9e9e', secondary: '#e0e0e0' };
      
      if (block.planning_status === 'DRAFT') {
        color = { primary: '#f97316', secondary: '#ffedd5' }; // Orange
      } else if (block.planning_status === 'APPROVED') {
        color = { primary: '#a855f7', secondary: '#f3e8ff' }; // Purple
      } else if (block.planning_status === 'CONFIRMED') {
        color = block.is_guaranteed
          ? { primary: '#4f46e5', secondary: '#e0e7ff' } // Dark Indigo
          : { primary: '#0284c7', secondary: '#e0f2fe' }; // Sky Blue
      }

      updatedEvents.push({
        id: `planned-${block.lecture_block_id}`,
        start: new Date(block.date),
        end: new Date(block.date),
        title: `${block.begin_time.substring(0, 5)} - ${block.end_time.substring(0, 5)} (${block.module})`,
        color,
        availabilityStatus: status,
        variantId: 0,
        variantName: block.module,
        slotId: `planned-${block.lecture_block_id}`,
        locationIds: [], 
        isPlanned: true,
        planningStatus: block.planning_status,
        isGuaranteed: block.is_guaranteed,
        slots: [{ 
          module_variant_slot_id: 0,
          fk_module_variant_id: 0,
          day_of_week: 0,
          start_time: block.begin_time,
          end_time: block.end_time,
          order_index: 0
        } as any],
        meta: {
          availabilityStatus: status,
          isPlanned: true,
          planningStatus: block.planning_status,
          isGuaranteed: block.is_guaranteed,
          locationShortname: block.location
        }
      } as AvailabilityEvent);
    });

    const allEvents = [...updatedEvents];
    
    // Group planned events by date and time window
    const plannedTimeWindows = new Set<string>();
    allEvents.forEach(e => {
        if ((e as any).isPlanned) {
            const dateStr = format(e.start, 'yyyy-MM-dd');
            const begin = e.slots?.[0]?.start_time?.substring(0, 5) || '';
            const end = e.slots?.[0]?.end_time?.substring(0, 5) || '';
            plannedTimeWindows.add(`${dateStr}|${begin}|${end}`);
        }
    });

    const finalEvents = allEvents.filter(e => {
        if (e.isHoliday || (e as any).isPlanned) return true;
        const dateStr = format(e.start, 'yyyy-MM-dd');
        const begin = e.slots?.[0]?.start_time?.substring(0, 5) || '';
        const end = e.slots?.[0]?.end_time?.substring(0, 5) || '';
        const key = `${dateStr}|${begin}|${end}`;
        return !plannedTimeWindows.has(key);
    });

    this.calendarEvents.set(finalEvents);
  }
}
